import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  TextField,
  Grid,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import HistoryIcon from "@mui/icons-material/History";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InfoIcon from "@mui/icons-material/Info";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import PendingIcon from "@mui/icons-material/Pending";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { getThemeTextColor, getThemeBackgroundColor } from "../../utils/themeUtils";
import { useTheme } from "@mui/material/styles";
import Notification from "../../components/Notification";
import ConfirmDialog from "../../components/ConfirmDialog";
import apiUtils from "../../utils/apiUtils";
import axios from "axios";
import { GlobalContext } from "../../contexts/GlobalContext";
import moment from "moment";
import JSZip from "jszip";

// Component for displaying required documents upload interface
const RequiredDocumentsUpload = ({
  countryName,
  countryCode,
  docName,
  onUpload,
  onClose,
  documentTypes,
  countryData,
  existingDocs = [],
  showUploadInterface = false,
  customerDocuments = [],
  customerId,
  inlineMode = false,
  specificDocumentType = null,
  specialRequirements = {},
  allCountryRequirements = null,
  onShowDateInputDialog = null,
  onDateInputCancel = null,
  setNotify = null,
}) => {
  const theme = useTheme();
  const [countryRequirements, setCountryRequirements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const loadRequirements = () => {
      try {
        const docType = documentTypes?.find((doc) => doc.name === docName);
        const country = countryData?.find((c) => c.countryCode === countryCode);

        if (docType && country) {
          // Use cached special requirements instead of API call
          const requirementKey = `${country.countryId}-${docType.documentTypeId}`;
          const requirementType = specialRequirements[requirementKey];

          // Check if this country-document combination has time restrictions
          // This can be from document type OR from country requirements
          const hasTimeRestriction =
            docType.hasTimeRestriction ||
            (allCountryRequirements &&
              allCountryRequirements.some(
                (req) =>
                  req.countryId === country.countryId &&
                  req.documentTypeId === docType.documentTypeId &&
                  (req.hasTimeRestriction || req.hasTimRestriction)
              ));

          // Create a mock country requirement object with the special requirements
          const countryRequirement = {
            countryId: country.countryId,
            documentTypeId: docType.documentTypeId,
            specialRequirements: requirementType,
            hasTimeRestriction: hasTimeRestriction,
          };

          setCountryRequirements(countryRequirement);
        }
      } catch (error) {
        setNotify({
          isOpen: true,
          message: "Failed to load country requirements. Please try again.",
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    loadRequirements();
  }, [docName, countryCode, specialRequirements, allCountryRequirements]);

  // Reset selected files when customerDocuments change (e.g., after upload or delete)
  useEffect(() => {
    setSelectedFiles({});
  }, [customerDocuments]);

  // Force re-render when customerDocuments or existingDocs change
  const [renderKey, setRenderKey] = useState(0);
  useEffect(() => {
    setRenderKey((prev) => prev + 1);
  }, [customerDocuments, existingDocs]);

  const handleFileSelect = (documentType, file) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [documentType]: file,
    }));

    // Check if time restrictions are required
    const requiredDocs = getRequiredDocuments();
    const doc = requiredDocs.find((d) => d.type === documentType);

    if (doc) {
      // Check if this document type has time restrictions
      const docType = documentTypes?.find((dt) => dt.documentTypeId === doc.documentTypeId);
      const country = countryData?.find((c) => c.countryCode === countryCode);

      const hasTimeRestriction =
        docType?.hasTimeRestriction ||
        (allCountryRequirements &&
          country &&
          allCountryRequirements.some(
            (req) =>
              req.countryId === country.countryId &&
              req.documentTypeId === doc.documentTypeId &&
              (req.hasTimeRestriction || req.hasTimRestriction)
          ));

      if (hasTimeRestriction) {
        // Show date input popup via callback
        if (onShowDateInputDialog) {
          onShowDateInputDialog({
            documentType,
            file,
            countryCode,
            documentCode: doc.code,
            documentTypeId: doc.documentTypeId,
          });
        }
      } else {
        // No time restrictions required, upload immediately
        onUpload(countryCode, doc.code, file, doc.documentTypeId, null);
      }
    }
  };

  const handleDateInputCancel = () => {
    // Reset all selected files when date input is cancelled
    setSelectedFiles({});
  };

  // Expose the handleDateInputCancel function to the parent
  useEffect(() => {
    if (onDateInputCancel) {
      onDateInputCancel(handleDateInputCancel);
    }
  }, [onDateInputCancel]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e, documentType) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(documentType, files[0]);
    }
  };

  const getRequiredDocuments = () => {
    const country = countryData?.find((c) => c.countryCode === countryCode);
    if (!country) {
      return [];
    }

    const documents = [];

    // Use existingDocs (which is viewDialog.docs) instead of customerDocuments
    // This ensures we have the most up-to-date list after delete operations
    const countryDocuments = existingDocs || [];

    // If specificDocumentType is provided, always return that document type for upload
    if (specificDocumentType) {
      let docInfo = {};

      if (Number(specificDocumentType) === 998) {
        docInfo = {
          type: "legalization",
          name: "Legalization Document",
          code: "legalization",
          documentTypeId: 998,
        };
      } else if (Number(specificDocumentType) === 999) {
        docInfo = {
          type: "kvk",
          name: "KvK Translated Documents",
          code: "kvkTranslated",
          documentTypeId: 999,
        };
      } else {
        // Main document
        const mainDocType = documentTypes?.find((doc) => doc.documentTypeId === Number(specificDocumentType));
        if (mainDocType) {
          docInfo = {
            type: "main",
            name: mainDocType.name,
            code: mainDocType.shortCode?.toLowerCase(),
            documentTypeId: mainDocType.documentTypeId,
          };
        }
      }

      if (docInfo.documentTypeId) {
        documents.push(docInfo);
      }
      return documents;
    }

    // Original logic for showing all required documents
    // Check main document - only show if not present
    const mainDocType = documentTypes?.find((doc) => doc.name === docName);
    if (mainDocType) {
      const hasMainDoc = countryDocuments.some((doc) => Number(doc.documentType) === Number(mainDocType.documentTypeId));
      if (!hasMainDoc) {
        documents.push({
          type: "main",
          name: docName,
          code: mainDocType.shortCode?.toLowerCase(),
          documentTypeId: mainDocType.documentTypeId,
        });
      }
    }

    // Use pre-calculated special requirements, with fallback to countryRequirements
    if (mainDocType) {
      const requirementKey = `${country.countryId}-${mainDocType.documentTypeId}`;
      let requirementType = specialRequirements[requirementKey];

      // Fallback: if specialRequirements is not available yet, use countryRequirements
      if (!requirementType && countryRequirements?.specialRequirements) {
        requirementType = countryRequirements.specialRequirements;
      }

      if (requirementType) {
        if (["1", "2", "3"].includes(requirementType)) {
          // Check legalization document - only show if not present
          const hasLegalizationDoc = countryDocuments.some((doc) => Number(doc.documentType) === 998);
          if (!hasLegalizationDoc) {
            documents.push({
              type: "legalization",
              name: "Legalization Document",
              code: "legalization",
              documentTypeId: 998,
            });
          }
        }

        if (["3", "4"].includes(requirementType)) {
          // Check KvK document - only show if not present
          const hasKvkDoc = countryDocuments.some((doc) => Number(doc.documentType) === 999);
          if (!hasKvkDoc) {
            documents.push({
              type: "kvk",
              name: "KvK Translated Documents",
              code: "kvkTranslated",
              documentTypeId: 999,
            });
          }
        }
      }
    }
    return documents;
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography sx={{ ...getThemeTextColor(theme) }}>Loading requirements...</Typography>
      </Box>
    );
  }

  const documents = getRequiredDocuments();

  const getRequirementDescription = () => {
    if (!countryRequirements?.specialRequirements) return null;

    const reqType = countryRequirements.specialRequirements;
    if (reqType === "1") return "Legalization (Document Type 998)";
    if (reqType === "2") return "Legalization (Document Type 998)";
    if (reqType === "3") return "Legalization (Document Type 998) + required KvK Translated Documents (Document Type 999)";
    if (reqType === "4") return "Required KvK Translated Documents (Document Type 999)";
    return null;
  };

  const requiredDocuments = getRequiredDocuments();

  // If showUploadInterface is true, always show the upload interface
  // Otherwise, only show if there are missing documents
  if (!showUploadInterface && requiredDocuments.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <CheckCircleIcon sx={{ color: "success.main", fontSize: 48, mb: 2 }} />
        <Typography variant="h6" gutterBottom color="success.main">
          All Required Documents Present
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          All required documents for {countryName} have been uploaded.
        </Typography>
      </Box>
    );
  }

  if (inlineMode) {
    // Inline mode: show upload kaders without header
    return (
      <Box key={renderKey}>
        {requiredDocuments.map((doc, index) => {
          // Determine if document is required based on requirement type
          const isRequired = () => {
            if (doc.type === "main") return true; // Main document is always required
            if (doc.type === "legalization") {
              return ["2", "3"].includes(countryRequirements?.specialRequirements);
            }
            if (doc.type === "kvk") {
              return ["3", "4"].includes(countryRequirements?.specialRequirements);
            }
            return false;
          };

          return (
            <Box key={doc.type} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                {doc.name}
                {isRequired() ? " *" : ""}
              </Typography>
              <Box
                sx={{
                  border: "2px dashed",
                  borderColor: selectedFiles[doc.type] ? "success.main" : "grey.300",
                  borderRadius: 2,
                  p: 2,
                  textAlign: "center",
                  bgcolor: selectedFiles[doc.type] ? "success.light" : "background.paper",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "primary.light",
                  },
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, doc.type)}
                onClick={() => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = ".pdf,.doc,.docx,.jpg,.jpeg,.png";
                  input.onchange = (e) => {
                    if (e.target.files[0]) {
                      handleFileSelect(doc.type, e.target.files[0]);
                    }
                  };
                  input.click();
                }}
              >
                {selectedFiles[doc.type] ? (
                  <Box>
                    <CheckCircleIcon sx={{ color: "success.main", fontSize: 40, mb: 1 }} />
                    <Typography variant="body2" color="success.dark">
                      {selectedFiles[doc.type].name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Click to change file
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <CloudUploadIcon sx={{ color: "grey.400", fontSize: 40, mb: 1 }} />
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Click to upload {doc.name.toLowerCase()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      or drag and drop (auto-uploads)
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom color="primary">
          Required Documents
        </Typography>
        {getRequirementDescription() && (
          <Typography variant="body2" color="primary" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
            Requirement Type: {getRequirementDescription()}
          </Typography>
        )}
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Upload the following documents for {countryName}:
        </Typography>

        {requiredDocuments.map((doc, index) => {
          // Determine if document is required based on requirement type
          const isRequired = () => {
            if (doc.type === "main") return true; // Main document is always required
            if (doc.type === "legalization") {
              return ["2", "3"].includes(countryRequirements?.specialRequirements);
            }
            if (doc.type === "kvk") {
              return ["3", "4"].includes(countryRequirements?.specialRequirements);
            }
            return false;
          };

          return (
            <Box key={doc.type} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                {doc.name}
                {isRequired() ? " *" : ""}
              </Typography>
              <Box
                sx={{
                  border: "2px dashed",
                  borderColor: selectedFiles[doc.type] ? "success.main" : "grey.300",
                  borderRadius: 2,
                  p: 2,
                  textAlign: "center",
                  bgcolor: selectedFiles[doc.type] ? "success.light" : "background.paper",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "primary.light",
                  },
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, doc.type)}
                onClick={() => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = ".pdf,.doc,.docx,.jpg,.jpeg,.png";
                  input.onchange = (e) => {
                    if (e.target.files[0]) {
                      handleFileSelect(doc.type, e.target.files[0]);
                    }
                  };
                  input.click();
                }}
              >
                {selectedFiles[doc.type] ? (
                  <Box>
                    <CheckCircleIcon sx={{ color: "success.main", fontSize: 40, mb: 1 }} />
                    <Typography variant="body2" color="success.dark">
                      {selectedFiles[doc.type].name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Click to change file
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <CloudUploadIcon sx={{ color: "grey.400", fontSize: 40, mb: 1 }} />
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Click to upload {doc.name.toLowerCase()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      or drag and drop (auto-uploads)
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </>
  );
};

const MSRDocumentsTab = ({ customerId }) => {
  const theme = useTheme();
  const [notify, setNotify] = useState({ isOpen: false, message: "", type: "" });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: "", subTitle: "", onConfirm: null });
  const [uploadDialog, setUploadDialog] = useState({ isOpen: false, countryCode: "", documentCode: "" });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [customerDocuments, setCustomerDocuments] = useState([]);
  const { globalData } = useContext(GlobalContext);
  const uploadedBy = globalData?.userId;
  const [historyDialog, setHistoryDialog] = useState({ open: false, docs: [], docName: "", countryName: "" });
  const [historyFilenames, setHistoryFilenames] = useState({});
  const [historyPreviewDialog, setHistoryPreviewDialog] = useState({ open: false, doc: null, docName: "", countryName: "" });
  const [historyPreviewUrl, setHistoryPreviewUrl] = useState(null);
  const [viewDialog, setViewDialog] = useState({ open: false, docs: [], docName: "", countryName: "", countryCode: "" });
  const [selectedDocumentForPreview, setSelectedDocumentForPreview] = useState(null);
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState(null);
  const [contextMenu, setContextMenu] = useState({ mouseX: null, mouseY: null, countryCode: "", documentCode: "", docs: [] });

  // State for date input dialog
  const [dateInputDialog, setDateInputDialog] = useState({
    open: false,
    documentType: null,
    file: null,
    countryCode: "",
    documentCode: "",
    documentTypeId: null,
    onCancel: null,
  });

  // Store the reset callback from RequiredDocumentsUpload
  const [resetCallback, setResetCallback] = useState(null);

  const [timeRestrictions, setTimeRestrictions] = useState({});

  // State for document status checks
  const [vatStatus, setVatStatus] = useState({});
  const [exciseStatus, setExciseStatus] = useState({});
  const [documentStatus, setDocumentStatus] = useState({});
  const [specialRequirements, setSpecialRequirements] = useState({});
  const [allCountryRequirements, setAllCountryRequirements] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // New state for multiple upload dialogs
  const [timeRestrictionDialog, setTimeRestrictionDialog] = useState({
    isOpen: false,
    countryCode: "",
    documentCode: "",
    validFrom: null,
    validTill: null,
  });
  const [specialRequirementsDialog, setSpecialRequirementsDialog] = useState({
    isOpen: false,
    countryCode: "",
    documentCode: "",
    requirementType: "",
    hasTimeRestriction: false,
    files: {
      mainDocument: null,
      legalizationDocument: null,
      kvkDocument: null,
    },
    validFrom: null,
    validTill: null,
  });

  // Get countries and document types from API
  const countryData = apiUtils.useApiData("/api/v1/countries");
  const documentTypes = apiUtils.useApiData("/api/v1/documenttypes");
  const dwsUserData = apiUtils.useApiData("/api/v1/GetDWSUsers");

  // Create document type map from API data
  const documentTypeMap = React.useMemo(() => {
    if (!documentTypes) return {};
    return documentTypes.reduce((acc, doc) => {
      acc[doc.shortCode.toLowerCase()] = doc.documentTypeId;
      return acc;
    }, {});
  }, [documentTypes]);

  // Helper function to enrich customer documents with hasTimeRestriction property
  const enrichCustomerDocuments = (documents) => {
    if (!documents || !allCountryRequirements || !documentTypes) return documents;

    return documents.map((doc) => {
      // Find the document type
      const docType = documentTypes.find((dt) => dt.documentTypeId === Number(doc.documentType));

      // Check if this document has time restrictions based on country requirements and document types
      const hasTimeRestriction =
        (docType && docType.hasTimeRestriction) ||
        allCountryRequirements.some(
          (req) =>
            req.countryId === Number(doc.countryId) &&
            req.documentTypeId === Number(doc.documentType) &&
            (req.hasTimeRestriction || req.hasTimRestriction)
        );

      return {
        ...doc,
        hasTimeRestriction: hasTimeRestriction,
      };
    });
  };

  // Fetch customer documents from API
  useEffect(() => {
    if (!customerId) return;
    const fetchDocuments = async () => {
      try {
        const response = await axios.get(`/api/v1/customerdocuments/entity/${customerId}`);
        const enrichedDocuments = enrichCustomerDocuments(response.data || []);
        setCustomerDocuments(enrichedDocuments);
      } catch (error) {
        setCustomerDocuments([]);
        setNotify({
          isOpen: true,
          message: "Failed to load customer documents. Please try again.",
          type: "error",
        });
      }
    };
    fetchDocuments();
  }, [customerId, allCountryRequirements, documentTypes]);

  // Fetch country requirements once when component mounts
  useEffect(() => {
    const fetchCountryRequirements = async () => {
      if (allCountryRequirements) {
        return;
      }

      try {
        const response = await axios.get(`/api/v1/countryrequirements`);
        const requirements = response.data || [];
        setAllCountryRequirements(requirements);
      } catch (error) {
        setNotify({
          isOpen: true,
          message: "Failed to load country requirements. Please refresh the page.",
          type: "error",
        });
      }
    };

    fetchCountryRequirements();
  }, []); // Only run once when component mounts

  // Check document status for all countries when data changes
  useEffect(() => {
    const checkAllDocumentStatus = () => {
      if (!countryData.length || !documentTypes.length || !allCountryRequirements) return;

      // If no customer documents, set all statuses to false
      if (!customerDocuments.length) {
        const emptyVatStatus = {};
        const emptyExciseStatus = {};
        const emptyDocumentStatus = {};
        const emptySpecialRequirements = {};

        // Build special requirements lookup from cached data
        allCountryRequirements.forEach((req) => {
          const key = `${req.countryId}-${req.documentTypeId}`;
          emptySpecialRequirements[key] = req.specialRequirements || null;
        });

        // Set all countries to false status
        countryData.forEach((country) => {
          emptyVatStatus[country.countryId] = false;
          emptyExciseStatus[country.countryId] = false;

          documentTypes.forEach((docType) => {
            const key = `${country.countryId}-${docType.documentTypeId}`;
            emptyDocumentStatus[key] = false;
          });
        });

        setVatStatus(emptyVatStatus);
        setExciseStatus(emptyExciseStatus);
        setDocumentStatus(emptyDocumentStatus);
        setSpecialRequirements(emptySpecialRequirements);
        return;
      }

      const newVatStatus = {};
      const newExciseStatus = {};
      const newDocumentStatus = {};
      const newSpecialRequirements = {};

      // Build special requirements lookup from cached data
      allCountryRequirements.forEach((req) => {
        const key = `${req.countryId}-${req.documentTypeId}`;
        newSpecialRequirements[key] = req.specialRequirements || null;
      });

      setSpecialRequirements(newSpecialRequirements);

      // Calculate statuses synchronously
      for (const country of countryData) {
        // Check VAT status - now considers vatRequiredCountries
        const vatRequiredDocTypes = documentTypes.filter((doc) => {
          if (!doc.vatRequired) return false;

          // Check if this country is in the vatRequiredCountries list
          if (doc.vatRequiredCountries) {
            const vatCountries = Array.isArray(doc.vatRequiredCountries)
              ? doc.vatRequiredCountries
              : doc.vatRequiredCountries.split(",").filter((id) => id.trim());
            // If country is NOT in the list, then it's NOT required for this country
            return vatCountries.includes(country.countryId.toString());
          }

          // If no vatRequiredCountries specified, use the old logic
          return true;
        });

        let hasAllVat = vatRequiredDocTypes.length > 0;

        for (const docType of vatRequiredDocTypes) {
          if (!isDocumentExists(country.countryId, docType.documentTypeId)) {
            hasAllVat = false;
            break;
          }

          const requirementKey = `${country.countryId}-${docType.documentTypeId}`;
          const requirementType = newSpecialRequirements[requirementKey];

          if (requirementType) {
            if (["1", "2", "3"].includes(requirementType) && !isDocumentExists(country.countryId, 998)) {
              hasAllVat = false;
              break;
            }
            if (["3", "4"].includes(requirementType) && !isDocumentExists(country.countryId, 999)) {
              hasAllVat = false;
              break;
            }
          }
        }
        newVatStatus[country.countryId] = hasAllVat;

        // Check Excise status - now considers edRequiredCountries
        const edRequiredDocTypes = documentTypes.filter((doc) => {
          if (!doc.edRequired) return false;

          // Check if this country is in the edRequiredCountries list
          if (doc.edRequiredCountries) {
            const edCountries = Array.isArray(doc.edRequiredCountries)
              ? doc.edRequiredCountries
              : doc.edRequiredCountries.split(",").filter((id) => id.trim());
            // If country is NOT in the list, then it's NOT required for this country
            return edCountries.includes(country.countryId.toString());
          }

          // If no edRequiredCountries specified, use the old logic
          return true;
        });

        let hasAllExcise = edRequiredDocTypes.length > 0;

        for (const docType of edRequiredDocTypes) {
          if (!isDocumentExists(country.countryId, docType.documentTypeId)) {
            hasAllExcise = false;
            break;
          }

          const requirementKey = `${country.countryId}-${docType.documentTypeId}`;
          const requirementType = newSpecialRequirements[requirementKey];

          if (requirementType) {
            if (["1", "2", "3"].includes(requirementType) && !isDocumentExists(country.countryId, 998)) {
              hasAllExcise = false;
              break;
            }
            if (["3", "4"].includes(requirementType) && !isDocumentExists(country.countryId, 999)) {
              hasAllExcise = false;
              break;
            }
          }
        }
        newExciseStatus[country.countryId] = hasAllExcise;

        // Check document type status
        for (const docType of documentTypes) {
          const key = `${country.countryId}-${docType.documentTypeId}`;
          const requirementType = newSpecialRequirements[key];

          // Check if main document exists and get its validity status
          const mainDocStatus = getDocumentStatus(country.countryId, docType.documentTypeId);
          const hasMainDocument = mainDocStatus?.exists || false;

          // Check if any special requirement documents exist and their validity status
          let hasSpecialRequirements = false;
          let specialReqStatus = "valid";
          let specialReqDocumentsExist = false;

          if (requirementType) {
            if (["1", "2", "3"].includes(requirementType)) {
              const doc998Status = getDocumentStatus(country.countryId, 998);
              if (doc998Status?.exists) {
                hasSpecialRequirements = true;
                specialReqDocumentsExist = true;
                if (doc998Status.status === "expired") {
                  specialReqStatus = "expired";
                } else if (doc998Status.status === "expiring" && specialReqStatus !== "expired") {
                  specialReqStatus = "expiring";
                } else if (doc998Status.status === "valid" && specialReqStatus === "valid") {
                  specialReqStatus = "valid";
                }
              }
            }
            if (["3", "4"].includes(requirementType)) {
              const doc999Status = getDocumentStatus(country.countryId, 999);
              if (doc999Status?.exists) {
                hasSpecialRequirements = true;
                specialReqDocumentsExist = true;
                if (doc999Status.status === "expired") {
                  specialReqStatus = "expired";
                } else if (doc999Status.status === "expiring" && specialReqStatus !== "expired") {
                  specialReqStatus = "expiring";
                }
              }
            }
          }

          // Determine status with validity checking:
          // - false: No documents at all
          // - true: All required documents present and valid
          // - "partial": Only some documents present
          // - "expiring": Documents expiring within 30 days
          // - "expired": Documents have expired
          if (!hasMainDocument && !hasSpecialRequirements) {
            newDocumentStatus[key] = false; // No documents
          } else if (hasMainDocument && (!requirementType || hasAllSpecialRequirements(country.countryId, requirementType))) {
            // All required documents exist - check validity
            // Priority: expired > expiring > valid
            // Check both main document and special requirement documents
            const mainStatus = mainDocStatus?.status || "valid";
            const combinedStatus =
              mainStatus === "expired" || specialReqStatus === "expired"
                ? "expired"
                : mainStatus === "expiring" || specialReqStatus === "expiring"
                ? "expiring"
                : "valid";

            if (combinedStatus === "expired") {
              newDocumentStatus[key] = "expired";
            } else if (combinedStatus === "expiring") {
              newDocumentStatus[key] = "expiring";
            } else {
              newDocumentStatus[key] = true;
            }
          } else {
            newDocumentStatus[key] = "partial"; // Partial - some documents missing
          }
        }
      }

      setVatStatus(newVatStatus);
      setExciseStatus(newExciseStatus);
      setDocumentStatus(newDocumentStatus);
    };

    checkAllDocumentStatus();
  }, [countryData, documentTypes, customerDocuments, allCountryRequirements]);

  // Fetch filenames when opening history dialog
  useEffect(() => {
    const fetchFilenames = async () => {
      if (!historyDialog.open || !historyDialog.docs.length) return;
      const newFilenames = {};
      await Promise.all(
        historyDialog.docs.map(async (doc) => {
          try {
            const res = await axios.get(`/api/v1/file/${doc.fileId}`);
            newFilenames[doc.fileId] = res.data?.fileName || "";
            // Add mimeType to the document object for preview functionality
            if (res.data?.mimeType) {
              doc.mimeType = res.data.mimeType;
            }
          } catch {
            newFilenames[doc.fileId] = "";
          }
        })
      );
      setHistoryFilenames(newFilenames);
    };
    fetchFilenames();
  }, [historyDialog]);

  // Fetch filenames when opening view dialog
  useEffect(() => {
    const fetchFilenames = async () => {
      if (!viewDialog.open || !viewDialog.docs.length) return;
      const newFilenames = {};
      await Promise.all(
        viewDialog.docs.map(async (doc) => {
          try {
            const res = await axios.get(`/api/v1/file/${doc.fileId}`);
            newFilenames[doc.fileId] = res.data?.fileName || "";
            // Add mimeType to the document object for preview functionality
            if (res.data?.mimeType) {
              doc.mimeType = res.data.mimeType;
            }
          } catch {
            newFilenames[doc.fileId] = "";
          }
        })
      );
      setHistoryFilenames(newFilenames);
    };
    fetchFilenames();
  }, [viewDialog]);

  // Helper function removed - now using cached special requirements instead of API calls

  // Helper function to determine which upload dialog to show
  const determineUploadDialogType = async (countryCode, documentCode) => {
    const country = countryData?.find((c) => c.countryCode === countryCode);
    const docType = documentTypes?.find((d) => d.shortCode.toLowerCase() === documentCode.toLowerCase());

    if (!country || !docType) return "default";

    // Use cached special requirements instead of API call
    const requirementKey = `${country.countryId}-${docType.documentTypeId}`;
    const requirementType = specialRequirements[requirementKey];

    // Check for time restrictions (both document type and country-specific)
    const hasTimeRestriction =
      docType.hasTimeRestriction ||
      (allCountryRequirements &&
        allCountryRequirements.some(
          (req) =>
            req.countryId === country.countryId &&
            req.documentTypeId === docType.documentTypeId &&
            (req.hasTimeRestriction || req.hasTimRestriction)
        ));

    if (requirementType && requirementType !== "") {
      return {
        type: "specialRequirements",
        requirementType: requirementType,
        hasTimeRestriction: hasTimeRestriction,
      };
    } else if (hasTimeRestriction) {
      return { type: "timeRestriction" };
    }

    return { type: "default" };
  };

  const handleFileUpload = async (file, countryCode, documentCode, documentTypeId = null, timeRestrictionData = null) => {
    if (!file || !customerId || !uploadedBy) return;

    setNotify({ isOpen: true, message: "Uploading...", type: "info" });

    try {
      const country = countryData?.find((c) => c.countryCode === countryCode);
      const countryId = country ? country.countryId : null;
      if (!countryId) {
        setNotify({ isOpen: true, message: "Country not found.", type: "error" });
        return;
      }

      // Use documentTypeId if provided, otherwise fall back to documentTypeMap
      const docType = documentTypeId || documentTypeMap[documentCode.toLowerCase()];
      const fileId = await uploadFileToAzure(file, documentCode);

      // Handle time restriction data
      const validFrom = timeRestrictionData?.validFrom
        ? moment(timeRestrictionData.validFrom).format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z"
        : null;
      const validTill = timeRestrictionData?.validTill
        ? moment(timeRestrictionData.validTill).format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z"
        : null;

      await createCustomerDocument(fileId, countryId, docType, validFrom, validTill);

      setNotify({ isOpen: true, message: "Document uploaded successfully!", type: "success" });

      // Refresh documents
      const refreshResponse = await axios.get(`/api/v1/customerdocuments/entity/${customerId}`);
      const updatedDocuments = enrichCustomerDocuments(refreshResponse.data || []);
      setCustomerDocuments(updatedDocuments);

      // Force DataGrid refresh
      setRefreshKey((prev) => prev + 1);

      // Also refresh the view dialog if it's open
      if (viewDialog.open) {
        // Get all documents for the current country and document type combination
        const country = countryData?.find((c) => c.countryCode === viewDialog.countryCode);
        const mainDocType = documentTypes?.find((doc) => doc.name === viewDialog.docName);

        if (country && mainDocType) {
          const updatedDocs = updatedDocuments.filter(
            (doc) =>
              Number(doc.entityId) === Number(customerId) &&
              Number(doc.countryId) === Number(country.countryId) &&
              Number(doc.deleted) === 0 &&
              (Number(doc.documentType) === Number(mainDocType.documentTypeId) ||
                Number(doc.documentType) === 998 || // Legalization
                Number(doc.documentType) === 999) // KvK
          );

          setViewDialog((prev) => ({
            ...prev,
            docs: updatedDocs,
          }));

          // Auto-select the newly uploaded document for preview
          if (updatedDocs.length > 0) {
            // Find the most recently uploaded document (highest documentVersion or most recent uploadDate)
            const newestDoc = updatedDocs.reduce((latest, current) => {
              if (current.documentVersion > latest.documentVersion) {
                return current;
              } else if (current.documentVersion === latest.documentVersion) {
                // If versions are equal, compare upload dates
                return new Date(current.uploadDate) > new Date(latest.uploadDate) ? current : latest;
              }
              return latest;
            });

            // Set the newest document as selected for preview
            setSelectedDocumentForPreview(newestDoc);

            // Generate preview URL for the selected document
            try {
              const previewResponse = await axios.get(`/api/v1/azureblobstorage/${newestDoc.fileId}`, {
                responseType: "blob",
              });
              const previewUrl = URL.createObjectURL(previewResponse.data);
              setDocumentPreviewUrl(previewUrl);
            } catch (error) {
              setNotify({
                isOpen: true,
                message: "Failed to generate document preview.",
                type: "error",
              });
              setDocumentPreviewUrl(null);
            }
          }
        }
      }
    } catch (error) {
      setNotify({ isOpen: true, message: `Error uploading document: ${error.message}`, type: "error" });
    }
  };

  const handleUpload = async (countryCode, documentCode, file = null, documentTypeId = null) => {
    if (file) {
      // Direct upload if file is provided
      await handleFileUpload(file, countryCode, documentCode, documentTypeId);
      return;
    }

    const dialogInfo = await determineUploadDialogType(countryCode, documentCode);

    switch (dialogInfo.type) {
      case "specialRequirements":
        setSpecialRequirementsDialog({
          isOpen: true,
          countryCode,
          documentCode,
          requirementType: dialogInfo.requirementType,
          hasTimeRestriction: dialogInfo.hasTimeRestriction || false,
          files: {
            mainDocument: null,
            legalizationDocument: null,
            kvkDocument: null,
          },
          validFrom: null,
          validTill: null,
        });
        break;
      case "timeRestriction":
        setTimeRestrictionDialog({
          isOpen: true,
          countryCode,
          documentCode,
          validFrom: null,
          validTill: null,
        });
        break;
      default:
        setUploadDialog({ isOpen: true, countryCode, documentCode });
        break;
    }
  };

  const isDocumentExists = (countryId, documentType) => {
    return customerDocuments.some(
      (doc) =>
        Number(doc.entityId) === Number(customerId) &&
        Number(doc.documentType) === Number(documentType) &&
        Number(doc.countryId) === Number(countryId) &&
        Number(doc.deleted) === 0
    );
  };

  // Helper function to check document validity status
  const getDocumentStatus = (countryId, documentType) => {
    const doc = customerDocuments.find(
      (d) =>
        Number(d.entityId) === Number(customerId) &&
        Number(d.documentType) === Number(documentType) &&
        Number(d.countryId) === Number(countryId) &&
        Number(d.deleted) === 0
    );

    if (!doc) return null;

    // Check if document has time restrictions
    if (!doc.validTill) return { exists: true, status: "valid" };

    const now = moment();
    const validTill = moment(doc.validTill);
    const daysUntilExpiry = validTill.diff(now, "days");

    if (daysUntilExpiry < 0) {
      return { exists: true, status: "expired" };
    } else if (daysUntilExpiry <= 30) {
      return { exists: true, status: "expiring" };
    } else {
      return { exists: true, status: "valid" };
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleCellDragOver = (event) => {
    event.preventDefault();
    event.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.04)";
  };

  const handleCellDragLeave = (event) => {
    event.preventDefault();
    event.currentTarget.style.backgroundColor = "";
  };

  const handleCellDrop = (event, countryCode, documentCode) => {
    event.preventDefault();
    event.currentTarget.style.backgroundColor = "";
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      handleUpload(countryCode, documentCode);
    }
  };

  // Date input dialog handlers
  const handleTimeRestrictionChange = (documentType, field, value) => {
    setTimeRestrictions((prev) => ({
      ...prev,
      [documentType]: {
        ...prev[documentType],
        [field]: value,
      },
    }));
  };

  const handleDateInputConfirm = () => {
    const { documentType, file, countryCode, documentCode, documentTypeId } = dateInputDialog;
    const timeRestrictionData = timeRestrictions[documentType];

    if (!timeRestrictionData?.validFrom || !timeRestrictionData?.validTill) {
      alert("Time restrictions are required. Please fill in both Valid From and Valid Till dates.");
      return;
    }

    // Call the upload function with time restriction data
    handleFileUpload(file, countryCode, documentCode, documentTypeId, timeRestrictionData);

    // Clear time restrictions for the current document type after successful upload
    if (dateInputDialog.documentType) {
      setTimeRestrictions((prev) => {
        const newRestrictions = { ...prev };
        delete newRestrictions[dateInputDialog.documentType];
        return newRestrictions;
      });
    }

    setDateInputDialog({ open: false, documentType: null, file: null, countryCode: "", documentCode: "", documentTypeId: null });
  };

  const handleDateInputCancel = () => {
    // Call the reset callback if provided
    if (resetCallback) {
      resetCallback();
    }

    // Clear time restrictions for the current document type
    if (dateInputDialog.documentType) {
      setTimeRestrictions((prev) => {
        const newRestrictions = { ...prev };
        delete newRestrictions[dateInputDialog.documentType];
        return newRestrictions;
      });
    }

    setDateInputDialog({
      open: false,
      documentType: null,
      file: null,
      countryCode: "",
      documentCode: "",
      documentTypeId: null,
      onCancel: null,
    });
  };

  // Helper function to get all relevant documents for a country and document type
  const getAllRelevantDocuments = (countryCode, documentCode) => {
    const country = countryData?.find((c) => c.countryCode === countryCode);
    const docType = documentTypes?.find((d) => d.shortCode.toLowerCase() === documentCode.toLowerCase());

    if (!country || !docType) return [];

    // Get all documents for this country and document type combination
    const allDocsForCountryAndType = customerDocuments.filter(
      (d) =>
        Number(d.countryId) === Number(country.countryId) &&
        Number(d.documentType) === Number(docType.documentTypeId) &&
        Number(d.deleted) === 0
    );

    // Use cached special requirements
    const requirementKey = `${country.countryId}-${docType.documentTypeId}`;
    const requirementType = specialRequirements[requirementKey];

    let allRelevantDocs = [...allDocsForCountryAndType];

    // If there are special requirements, add those documents too
    if (requirementType && requirementType !== "") {
      // Add legalization documents if required
      if (requirementType === "1" || requirementType === "2" || requirementType === "3") {
        const legalizationDocs = customerDocuments.filter(
          (d) =>
            Number(d.countryId) === Number(country.countryId) &&
            Number(d.documentType) === 998 && // Legalization document type ID
            Number(d.deleted) === 0
        );
        allRelevantDocs = [...allRelevantDocs, ...legalizationDocs];
      }

      // Add KvK Translated Documents if required
      if (requirementType === "3" || requirementType === "4") {
        const kvkDocs = customerDocuments.filter(
          (d) =>
            Number(d.countryId) === Number(country.countryId) &&
            Number(d.documentType) === 999 && // KvK Translated Documents type ID
            Number(d.deleted) === 0
        );
        allRelevantDocs = [...allRelevantDocs, ...kvkDocs];
      }
    }

    // Sort by document type and version for consistent display
    allRelevantDocs.sort((a, b) => {
      if (a.documentType !== b.documentType) {
        return a.documentType - b.documentType;
      }
      return b.documentVersion - a.documentVersion; // Newest first within same type
    });

    return allRelevantDocs;
  };

  // Helper function to get all relevant documents for history (including deleted)
  const getAllRelevantDocumentsForHistory = (countryCode, documentCode) => {
    const country = countryData?.find((c) => c.countryCode === countryCode);
    const docType = documentTypes?.find((d) => d.shortCode.toLowerCase() === documentCode.toLowerCase());

    if (!country || !docType) return [];

    // Get all documents for this country and document type combination (including deleted)
    const allDocsForCountryAndType = customerDocuments.filter(
      (d) => Number(d.countryId) === Number(country.countryId) && Number(d.documentType) === Number(docType.documentTypeId)
    );

    // Use cached special requirements
    const requirementKey = `${country.countryId}-${docType.documentTypeId}`;
    const requirementType = specialRequirements[requirementKey];

    let allRelevantDocs = [...allDocsForCountryAndType];

    // If there are special requirements, add those documents too (including deleted)
    if (requirementType && requirementType !== "") {
      // Add legalization documents if required
      if (requirementType === "1" || requirementType === "2" || requirementType === "3") {
        const legalizationDocs = customerDocuments.filter(
          (d) => Number(d.countryId) === Number(country.countryId) && Number(d.documentType) === 998 // Legalization document type ID
        );
        allRelevantDocs = [...allRelevantDocs, ...legalizationDocs];
      }

      // Add KvK Translated Documents if required
      if (requirementType === "3" || requirementType === "4") {
        const kvkDocs = customerDocuments.filter(
          (d) => Number(d.countryId) === Number(country.countryId) && Number(d.documentType) === 999 // KvK Translated Documents type ID
        );
        allRelevantDocs = [...allRelevantDocs, ...kvkDocs];
      }
    }

    // Sort by document type and version for consistent display
    allRelevantDocs.sort((a, b) => {
      if (a.documentType !== b.documentType) {
        return a.documentType - b.documentType;
      }
      return b.documentVersion - a.documentVersion; // Newest first within same type
    });

    return allRelevantDocs;
  };

  // Helper function to upload a single file and return fileId
  const uploadFileToAzure = async (file, documentCode) => {
    try {
      // Handle both string and numeric document types
      let documentTypeId = documentCode;
      if (typeof documentCode === "string") {
        const specialDocumentTypeMap = {
          legalization: 998,
          requiredLegalization: 998,
          kvkTranslated: 999,
          kvk: 999,
        };

        if (Object.prototype.hasOwnProperty.call(specialDocumentTypeMap, documentCode)) {
          documentTypeId = specialDocumentTypeMap[documentCode];
        } else {
          // For regular document types, use the document type map
          documentTypeId = documentTypeMap[documentCode.toLowerCase()];
        }
      }

      // 1. POST file info
      const fileInfo = {
        documentType: documentTypeId,
        creationDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
        createdBy: uploadedBy,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
      };
      const fileInfoRes = await axios.post("/api/v1/file", fileInfo);
      const fileId = fileInfoRes.data.fileId;

      // 2. POST file to Azure
      const formData = new FormData();
      formData.append("file", file, fileId);
      await axios.post("/api/v1/azureblobstorage", formData);

      return fileId;
    } catch (error) {
      throw new Error(`Error uploading file ${file.name}: ${error.message}`);
    }
  };

  // Helper function to create customer document record
  const createCustomerDocument = async (fileId, countryId, documentType, validFrom = null, validTill = null) => {
    try {
      const docs = customerDocuments.filter(
        (d) => Number(d.countryId) === Number(countryId) && Number(d.documentType) === Number(documentType)
        // Removed deleted filter to maintain version continuity
      );
      const latestVersion = docs.length > 0 ? Math.max(...docs.map((d) => d.documentVersion)) : 0;
      const nextVersion = latestVersion + 1;

      const documentInfo = {
        entityId: customerId,
        documentType: documentType,
        documentVersion: nextVersion,
        uploadedBy: uploadedBy,
        uploadDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
        fileId: fileId,
        deleted: 0,
        countryId: countryId,
        validFrom: validFrom,
        validTill: validTill,
      };

      await axios.post("/api/v1/customerdocuments", documentInfo);
      return nextVersion;
    } catch (error) {
      throw new Error(`Error creating customer document: ${error.message}`);
    }
  };

  const handleUploadConfirm = async () => {
    if (!selectedFile || !customerId || !uploadedBy) return;

    setNotify({ isOpen: true, message: "Uploading...", type: "info" });

    try {
      const country = countryData?.find((c) => c.countryCode === uploadDialog.countryCode);
      const countryId = country ? country.countryId : null;
      if (!countryId) {
        setNotify({ isOpen: true, message: "Country not found.", type: "error" });
        return;
      }

      const docType = documentTypeMap[uploadDialog.documentCode.toLowerCase()];
      const fileId = await uploadFileToAzure(selectedFile, uploadDialog.documentCode);
      await createCustomerDocument(fileId, countryId, docType);

      setNotify({ isOpen: true, message: "Document uploaded successfully!", type: "success" });
      setUploadDialog({ isOpen: false, countryCode: "", documentCode: "" });
      setSelectedFile(null);

      // Refresh documents
      const response = await axios.get(`/api/v1/customerdocuments/entity/${customerId}`);
      const enrichedDocuments = enrichCustomerDocuments(response.data || []);
      setCustomerDocuments(enrichedDocuments);
    } catch (error) {
      setNotify({ isOpen: true, message: `Error uploading document: ${error.message}`, type: "error" });
    }
  };

  // Handle time restriction upload
  const handleTimeRestrictionUpload = async () => {
    if (!selectedFile || !customerId || !uploadedBy) return;

    setNotify({ isOpen: true, message: "Uploading...", type: "info" });

    try {
      const country = countryData?.find((c) => c.countryCode === timeRestrictionDialog.countryCode);
      const countryId = country ? country.countryId : null;
      if (!countryId) {
        setNotify({ isOpen: true, message: "Country not found.", type: "error" });
        return;
      }

      const docType = documentTypeMap[timeRestrictionDialog.documentCode.toLowerCase()];
      const fileId = await uploadFileToAzure(selectedFile, timeRestrictionDialog.documentCode);

      const validFrom = timeRestrictionDialog.validFrom
        ? moment(timeRestrictionDialog.validFrom).format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z"
        : null;
      const validTill = timeRestrictionDialog.validTill
        ? moment(timeRestrictionDialog.validTill).format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z"
        : null;

      await createCustomerDocument(fileId, countryId, docType, validFrom, validTill);

      setNotify({ isOpen: true, message: "Document uploaded successfully!", type: "success" });
      setTimeRestrictionDialog({ isOpen: false, countryCode: "", documentCode: "", validFrom: null, validTill: null });
      setSelectedFile(null);

      // Refresh documents
      const response = await axios.get(`/api/v1/customerdocuments/entity/${customerId}`);
      const enrichedDocuments = enrichCustomerDocuments(response.data || []);
      setCustomerDocuments(enrichedDocuments);
    } catch (error) {
      setNotify({ isOpen: true, message: `Error uploading document: ${error.message}`, type: "error" });
    }
  };

  // Handle special requirements upload
  const handleSpecialRequirementsUpload = async () => {
    if (!specialRequirementsDialog.files.mainDocument || !customerId || !uploadedBy) return;

    setNotify({ isOpen: true, message: "Uploading...", type: "info" });

    try {
      const country = countryData?.find((c) => c.countryCode === specialRequirementsDialog.countryCode);
      const countryId = country ? country.countryId : null;
      if (!countryId) {
        setNotify({ isOpen: true, message: "Country not found.", type: "error" });
        return;
      }

      const docType = documentTypeMap[specialRequirementsDialog.documentCode.toLowerCase()];
      const requirementType = specialRequirementsDialog.requirementType;

      // Upload main document
      const mainFileId = await uploadFileToAzure(specialRequirementsDialog.files.mainDocument, specialRequirementsDialog.documentCode);

      const validFrom = specialRequirementsDialog.validFrom
        ? moment(specialRequirementsDialog.validFrom).format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z"
        : null;
      const validTill = specialRequirementsDialog.validTill
        ? moment(specialRequirementsDialog.validTill).format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z"
        : null;

      await createCustomerDocument(mainFileId, countryId, docType, validFrom, validTill);

      // Upload additional documents based on requirement type
      // Document Type 998: Legalization Document
      // Document Type 999: KvK Translated Documents
      if (requirementType === "1" && specialRequirementsDialog.files.legalizationDocument) {
        const legalizationFileId = await uploadFileToAzure(specialRequirementsDialog.files.legalizationDocument, "legalization");
        // Create a separate document record for legalization (ID: 998)
        await createCustomerDocument(legalizationFileId, countryId, 998, validFrom, validTill);
      }

      if (requirementType === "2" && specialRequirementsDialog.files.legalizationDocument) {
        const legalizationFileId = await uploadFileToAzure(specialRequirementsDialog.files.legalizationDocument, "legalization");
        // Create a separate document record for legalization (ID: 998)
        await createCustomerDocument(legalizationFileId, countryId, 998, validFrom, validTill);
      }

      if (requirementType === "3") {
        if (specialRequirementsDialog.files.legalizationDocument) {
          const legalizationFileId = await uploadFileToAzure(specialRequirementsDialog.files.legalizationDocument, "legalization");
          await createCustomerDocument(legalizationFileId, countryId, 998, validFrom, validTill);
        }
        if (specialRequirementsDialog.files.kvkDocument) {
          const kvkFileId = await uploadFileToAzure(specialRequirementsDialog.files.kvkDocument, "kvkTranslated");
          await createCustomerDocument(kvkFileId, countryId, 999, validFrom, validTill);
        }
      }

      if (requirementType === "4" && specialRequirementsDialog.files.kvkDocument) {
        const kvkFileId = await uploadFileToAzure(specialRequirementsDialog.files.kvkDocument, "kvkTranslated");
        await createCustomerDocument(kvkFileId, countryId, 999, validFrom, validTill);
      }

      setNotify({ isOpen: true, message: "Documents uploaded successfully!", type: "success" });
      setSpecialRequirementsDialog({
        isOpen: false,
        countryCode: "",
        documentCode: "",
        requirementType: "",
        hasTimeRestriction: false,
        files: {
          mainDocument: null,
          legalizationDocument: null,
          kvkDocument: null,
        },
        validFrom: null,
        validTill: null,
      });

      // Refresh documents
      const response = await axios.get(`/api/v1/customerdocuments/entity/${customerId}`);
      const enrichedDocuments = enrichCustomerDocuments(response.data || []);
      setCustomerDocuments(enrichedDocuments);
    } catch (error) {
      setNotify({ isOpen: true, message: `Error uploading documents: ${error.message}`, type: "error" });
    }
  };

  const handleUploadCancel = () => {
    setUploadDialog({ isOpen: false, countryCode: "", documentCode: "" });
    setSelectedFile(null);
  };

  const handleTimeRestrictionCancel = () => {
    setTimeRestrictionDialog({ isOpen: false, countryCode: "", documentCode: "", validFrom: null, validTill: null });
    setSelectedFile(null);
  };

  const handleSpecialRequirementsCancel = () => {
    setSpecialRequirementsDialog({
      isOpen: false,
      countryCode: "",
      documentCode: "",
      requirementType: "",
      hasTimeRestriction: false,
      files: {
        mainDocument: null,
        legalizationDocument: null,
        kvkDocument: null,
      },
      validFrom: null,
      validTill: null,
    });
  };

  const handleViewDocuments = async (docs, docName, countryName, countryCode = null) => {
    let country, docType;

    if (docs.length > 0) {
      // Get the country and document type from the first document
      const firstDoc = docs[0];
      country = countryData?.find((c) => c.countryId === firstDoc.countryId);
      docType = documentTypes?.find((d) => d.documentTypeId === firstDoc.documentType);
    } else if (countryCode) {
      // For upload mode, get country and document type from parameters
      country = countryData?.find((c) => c.countryCode === countryCode);
      docType = documentTypes?.find((d) => d.name === docName);
    }

    if (!country || !docType) return;

    // Get all documents for this country and document type combination
    const allDocsForCountryAndType = customerDocuments.filter(
      (d) =>
        Number(d.countryId) === Number(country.countryId) &&
        Number(d.documentType) === Number(docType.documentTypeId) &&
        Number(d.deleted) === 0
    );

    // Use cached special requirements instead of API call
    const requirementKey = `${country.countryId}-${docType.documentTypeId}`;
    const requirementType = specialRequirements[requirementKey];

    let allRelevantDocs = [...allDocsForCountryAndType];

    // If there are special requirements, add those documents too
    if (requirementType && requirementType !== "") {
      // Add legalization documents if required
      if (requirementType === "1" || requirementType === "2" || requirementType === "3") {
        // Legalization (Document Type 998)
        const legalizationDocs = customerDocuments.filter(
          (d) =>
            Number(d.countryId) === Number(country.countryId) &&
            Number(d.documentType) === 998 && // Legalization document type ID
            Number(d.deleted) === 0
        );
        allRelevantDocs = [...allRelevantDocs, ...legalizationDocs];
      }

      // Add KvK Translated Documents if required
      if (requirementType === "3" || requirementType === "4") {
        const kvkDocs = customerDocuments.filter(
          (d) =>
            Number(d.countryId) === Number(country.countryId) &&
            Number(d.documentType) === 999 && // KvK Translated Documents type ID
            Number(d.deleted) === 0
        );
        allRelevantDocs = [...allRelevantDocs, ...kvkDocs];
      }
    }

    // Remove duplicates and sort by document type and version
    const uniqueDocs = allRelevantDocs.reduce((acc, doc) => {
      const existingDoc = acc.find((d) => d.documentType === doc.documentType);
      if (!existingDoc || doc.documentVersion > existingDoc.documentVersion) {
        if (existingDoc) {
          acc = acc.filter((d) => d.documentType !== doc.documentType);
        }
        acc.push(doc);
      }
      return acc;
    }, []);

    // Sort by document type for consistent display
    uniqueDocs.sort((a, b) => a.documentType - b.documentType);

    setViewDialog({ open: true, docs: uniqueDocs, docName, countryName, countryCode: country.countryCode });
    // Set first document as default preview
    if (uniqueDocs.length > 0) {
      setSelectedDocumentForPreview(uniqueDocs[0]);
      handleDocumentPreviewSelect(uniqueDocs[0]);
    }
  };

  const handleDocumentPreviewSelect = async (doc) => {
    setSelectedDocumentForPreview(doc);
    try {
      // Get file blob for preview
      const response = await axios({
        url: `/api/v1/azureblobstorage/${doc.fileId}`,
        method: "GET",
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      setDocumentPreviewUrl(url);
    } catch (error) {
      setNotify({
        isOpen: true,
        message: "Failed to load document preview.",
        type: "error",
      });
      setDocumentPreviewUrl(null);
    }
  };

  const handleViewDialogClose = () => {
    setViewDialog({ open: false, docs: [], docName: "", countryName: "", countryCode: "" });
    setSelectedDocumentForPreview(null);
    if (documentPreviewUrl) {
      URL.revokeObjectURL(documentPreviewUrl);
      setDocumentPreviewUrl(null);
    }
  };

  const handleHistoryDocumentPreview = async (doc, docName, countryName) => {
    setHistoryPreviewDialog({ open: true, doc, docName, countryName });
    try {
      // Get file blob for preview
      const response = await axios({
        url: `/api/v1/azureblobstorage/${doc.fileId}`,
        method: "GET",
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      setHistoryPreviewUrl(url);
    } catch (error) {
      setNotify({
        isOpen: true,
        message: "Failed to load history document preview.",
        type: "error",
      });
      setHistoryPreviewUrl(null);
    }
  };

  const handleHistoryPreviewDialogClose = () => {
    setHistoryPreviewDialog({ open: false, doc: null, docName: "", countryName: "" });
    if (historyPreviewUrl) {
      URL.revokeObjectURL(historyPreviewUrl);
      setHistoryPreviewUrl(null);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      // Find the document to get its current data
      const docToDelete = customerDocuments.find((doc) => doc.customerDocumentId === documentId);
      if (!docToDelete) {
        throw new Error("Document not found");
      }

      // Use PUT to mark as deleted (soft delete)
      const deleteResponse = await axios.put(`/api/v1/customerdocuments/${documentId}`, {
        ...docToDelete,
        deleted: 1,
      });

      if (deleteResponse.status === 200 || deleteResponse.status === 204) {
        setNotify({ isOpen: true, message: "Document deleted successfully!", type: "success" });
        // Refresh the documents list
        const refreshResponse = await axios.get(`/api/v1/customerdocuments/entity/${customerId}`);
        const updatedDocuments = enrichCustomerDocuments(refreshResponse.data);

        setCustomerDocuments(updatedDocuments);

        // Force DataGrid refresh
        setRefreshKey((prev) => prev + 1);

        // Note: View dialog update is handled in the onConfirm callback for immediate UI feedback
      }
    } catch (error) {
      // Error notification is already set above
      setNotify({ isOpen: true, message: "Error deleting document.", type: "error" });
    }
  };

  const getDocumentName = (documentCode) => {
    // Handle both string and numeric document codes
    if (typeof documentCode === "string") {
      const doc = documentTypes?.find((d) => d.shortCode.toLowerCase() === documentCode.toLowerCase());
      return doc ? doc.name : documentCode;
    } else if (typeof documentCode === "number") {
      // For numeric document codes, find by documentTypeId
      const doc = documentTypes?.find((d) => d.documentTypeId === documentCode);
      if (doc) {
        return doc.name;
      }

      // Handle special document types with English names
      const specialDocumentTypeNames = {
        998: "Legalization Document",
        999: "KvK Translated Documents",
      };

      if (specialDocumentTypeNames[documentCode]) {
        return specialDocumentTypeNames[documentCode];
      }

      return `Document Type ${documentCode}`;
    }
    return documentCode;
  };

  const getCountryName = (countryCode) => {
    const country = countryData?.find((c) => c.countryCode === countryCode);
    return country ? country.countryName : countryCode;
  };

  const getRequirementDescription = (requirementType) => {
    const descriptions = {
      1: "Legalization required",
      2: "Legalization required",
      3: "Legalization + KvK Translation required",
      4: "KvK Translation required",
    };
    return descriptions[requirementType] || `Requirement type ${requirementType}`;
  };

  const getUserName = (userId) => {
    // If it's the current user, get name from global context
    if (Number(userId) === Number(globalData?.userId)) {
      return globalData?.displayName || "Current User";
    }

    // For other users, try to find in dwsUserData array
    if (!dwsUserData || dwsUserData.length === 0) {
      return `User ${userId}`;
    }

    // Try different field names that might exist
    let user = dwsUserData.find((u) => u.id === userId);
    if (!user) {
      user = dwsUserData.find((u) => Number(u.userId) === Number(userId));
    }
    if (!user) {
      user = dwsUserData.find((u) => Number(u.user_id) === Number(userId));
    }

    if (user) {
      // Use the correct field names from the API response - combine firstname and lastname
      return `${user.firstname} ${user.lastname}`;
    }

    return `User ${userId}`;
  };

  // Helper to check if ALL required documents are present for VAT
  const hasAllVatDocs = async (countryId) => {
    const vatRequiredDocTypes =
      documentTypes?.filter((doc) => {
        if (!doc.vatRequired) return false;

        // Check if this country is in the vatRequiredCountries list
        if (doc.vatRequiredCountries) {
          const vatCountries = Array.isArray(doc.vatRequiredCountries)
            ? doc.vatRequiredCountries
            : doc.vatRequiredCountries.split(",").filter((id) => id.trim());
          // If country is NOT in the list, then it's NOT required for this country
          return vatCountries.includes(countryId.toString());
        }

        // If no vatRequiredCountries specified, use the old logic
        return true;
      }) || [];

    for (const docType of vatRequiredDocTypes) {
      // Check if main document exists
      if (!isDocumentExists(countryId, docType.documentTypeId)) {
        return false;
      }

      // Check special requirements using pre-calculated data
      const requirementKey = `${countryId}-${docType.documentTypeId}`;
      const requirementType = specialRequirements[requirementKey];

      if (requirementType) {
        // Check legalization document if required
        if (["1", "2", "3"].includes(requirementType)) {
          if (!isDocumentExists(countryId, 998)) {
            return false;
          }
        }

        // Check KvK document if required
        if (["3", "4"].includes(requirementType)) {
          if (!isDocumentExists(countryId, 999)) {
            return false;
          }
        }
      }
    }

    return vatRequiredDocTypes.length > 0; // Only return true if there are VAT documents and all are complete
  };

  // Helper to check if ALL required documents are present for VAT (with requirements parameter)
  const hasAllVatDocsWithRequirements = async (countryId, requirements) => {
    const vatRequiredDocTypes =
      documentTypes?.filter((doc) => {
        if (!doc.vatRequired) return false;

        // Check if this country is in the vatRequiredCountries list
        if (doc.vatRequiredCountries) {
          const vatCountries = Array.isArray(doc.vatRequiredCountries)
            ? doc.vatRequiredCountries
            : doc.vatRequiredCountries.split(",").filter((id) => id.trim());
          // If country is NOT in the list, then it's NOT required for this country
          return vatCountries.includes(countryId.toString());
        }

        // If no vatRequiredCountries specified, use the old logic
        return true;
      }) || [];

    for (const docType of vatRequiredDocTypes) {
      // Check if main document exists
      if (!isDocumentExists(countryId, docType.documentTypeId)) {
        return false;
      }

      // Check special requirements using provided requirements
      const requirementKey = `${countryId}-${docType.documentTypeId}`;
      const requirementType = requirements[requirementKey];

      if (requirementType) {
        // Check legalization document if required
        if (["1", "2", "3"].includes(requirementType)) {
          if (!isDocumentExists(countryId, 998)) {
            return false;
          }
        }

        // Check KvK document if required
        if (["3", "4"].includes(requirementType)) {
          if (!isDocumentExists(countryId, 999)) {
            return false;
          }
        }
      }
    }

    return vatRequiredDocTypes.length > 0; // Only return true if there are VAT documents and all are complete
  };

  // Helper to check if ALL required documents are present for Excise
  const hasAllExciseDocs = async (countryId) => {
    const edRequiredDocTypes =
      documentTypes?.filter((doc) => {
        if (!doc.edRequired) return false;

        // Check if this country is in the edRequiredCountries list
        if (doc.edRequiredCountries) {
          const edCountries = Array.isArray(doc.edRequiredCountries)
            ? doc.edRequiredCountries
            : doc.edRequiredCountries.split(",").filter((id) => id.trim());
          // If country is NOT in the list, then it's NOT required for this country
          return edCountries.includes(countryId.toString());
        }

        // If no edRequiredCountries specified, use the old logic
        return true;
      }) || [];

    for (const docType of edRequiredDocTypes) {
      // Check if main document exists
      if (!isDocumentExists(countryId, docType.documentTypeId)) {
        return false;
      }

      // Check special requirements using pre-calculated data
      const requirementKey = `${countryId}-${docType.documentTypeId}`;
      const requirementType = specialRequirements[requirementKey];

      if (requirementType) {
        // Check legalization document if required
        if (["1", "2", "3"].includes(requirementType)) {
          if (!isDocumentExists(countryId, 998)) {
            return false;
          }
        }

        // Check KvK document if required
        if (["3", "4"].includes(requirementType)) {
          if (!isDocumentExists(countryId, 999)) {
            return false;
          }
        }
      }
    }

    return edRequiredDocTypes.length > 0; // Only return true if there are Excise documents and all are complete
  };

  // Helper to check if ALL required documents are present for Excise (with requirements parameter)
  const hasAllExciseDocsWithRequirements = async (countryId, requirements) => {
    const edRequiredDocTypes =
      documentTypes?.filter((doc) => {
        if (!doc.edRequired) return false;

        // Check if this country is in the edRequiredCountries list
        if (doc.edRequiredCountries) {
          const edCountries = Array.isArray(doc.edRequiredCountries)
            ? doc.edRequiredCountries
            : doc.edRequiredCountries.split(",").filter((id) => id.trim());
          // If country is NOT in the list, then it's NOT required for this country
          return edCountries.includes(countryId.toString());
        }

        // If no edRequiredCountries specified, use the old logic
        return true;
      }) || [];

    for (const docType of edRequiredDocTypes) {
      // Check if main document exists
      if (!isDocumentExists(countryId, docType.documentTypeId)) {
        return false;
      }

      // Check special requirements using provided requirements
      const requirementKey = `${countryId}-${docType.documentTypeId}`;
      const requirementType = requirements[requirementKey];

      if (requirementType) {
        // Check legalization document if required
        if (["1", "2", "3"].includes(requirementType)) {
          if (!isDocumentExists(countryId, 998)) {
            return false;
          }
        }

        // Check KvK document if required
        if (["3", "4"].includes(requirementType)) {
          if (!isDocumentExists(countryId, 999)) {
            return false;
          }
        }
      }
    }

    return edRequiredDocTypes.length > 0; // Only return true if there are Excise documents and all are complete
  };

  // Helper to check if all special requirements are met
  const hasAllSpecialRequirements = (countryId, requirementType) => {
    if (!requirementType) return true;

    if (["1", "2", "3"].includes(requirementType) && !isDocumentExists(countryId, 998)) {
      return false;
    }
    if (["3", "4"].includes(requirementType) && !isDocumentExists(countryId, 999)) {
      return false;
    }
    return true;
  };

  // Helper to check if ALL required documents are present for a specific document type
  const hasAllRequiredDocs = async (countryId, documentTypeId) => {
    // Check if main document exists
    if (!isDocumentExists(countryId, documentTypeId)) {
      return false;
    }

    // Check special requirements using pre-calculated data
    const requirementKey = `${countryId}-${documentTypeId}`;
    const requirementType = specialRequirements[requirementKey];

    if (requirementType) {
      // Check legalization document if required
      if (["1", "2", "3"].includes(requirementType)) {
        if (!isDocumentExists(countryId, 998)) {
          return false;
        }
      }

      // Check KvK document if required
      if (["3", "4"].includes(requirementType)) {
        if (!isDocumentExists(countryId, 999)) {
          return false;
        }
      }
    }
    // If no special requirements, main document is sufficient

    return true;
  };

  // Helper to check if ALL required documents are present for a specific document type (with requirements parameter)
  const hasAllRequiredDocsWithRequirements = async (countryId, documentTypeId, requirements) => {
    // Check if main document exists
    if (!isDocumentExists(countryId, documentTypeId)) {
      return false;
    }

    // Check special requirements using provided requirements
    const requirementKey = `${countryId}-${documentTypeId}`;
    const requirementType = requirements[requirementKey];

    if (requirementType) {
      // Check legalization document if required
      if (["1", "2", "3"].includes(requirementType)) {
        if (!isDocumentExists(countryId, 998)) {
          return false;
        }
      }

      // Check KvK document if required
      if (["3", "4"].includes(requirementType)) {
        if (!isDocumentExists(countryId, 999)) {
          return false;
        }
      }
    }
    // If no special requirements, main document is sufficient

    return true;
  };

  const downloadFile = async (fileId) => {
    try {
      // 1. Fetch file info to get the filename
      const fileInfoRes = await axios.get(`/api/v1/file/${fileId}`);
      const fileName = fileInfoRes.data?.fileName || "document.pdf";
      // 2. Fetch the file blob
      const response = await axios({
        url: "/api/v1/azureblobstorage/" + fileId,
        method: "GET",
        responseType: "blob",
      });
      const href = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = href;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
    } catch (error) {
      setNotify({ isOpen: true, message: "Error downloading file.", type: "error" });
      // Error notification is already set above
    }
  };

  // Download multiple files as zip
  const downloadFilesAsZip = async (docs, countryName, documentTypeName) => {
    try {
      setNotify({ isOpen: true, message: "Creating zip file...", type: "info" });

      const zip = new JSZip();
      const countryFolder = zip.folder(countryName);

      for (const doc of docs) {
        try {
          const fileInfoRes = await axios.get(`/api/v1/file/${doc.fileId}`);
          const fileName = fileInfoRes.data?.fileName || `document_${doc.documentVersion}.pdf`;

          const response = await axios({
            url: `/api/v1/azureblobstorage/${doc.fileId}`,
            method: "GET",
            responseType: "blob",
          });

          countryFolder.file(fileName, response.data);
        } catch (error) {
          // Skip files that can't be added to zip
        }
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const href = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `${countryName}-${documentTypeName}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);

      setNotify({ isOpen: false, message: "", type: "" });
      setTimeout(() => {
        setNotify({ isOpen: true, message: "Zip file downloaded successfully!", type: "success" });
      }, 100);
    } catch (error) {
      setNotify({ isOpen: true, message: "Error creating zip file.", type: "error" });
      // Error notification is already set above
    }
  };

  const handleContextMenu = (event, countryCode, documentCode, docs) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      countryCode,
      documentCode,
      docs,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu({ mouseX: null, mouseY: null, countryCode: "", documentCode: "", docs: [] });
  };

  const handleContextMenuAction = (action) => {
    const { countryCode, documentCode, docs } = contextMenu;

    switch (action) {
      case "upload":
        if (docs.length > 0) {
          // If documents exist, show view popup
          handleViewDocuments(docs, getDocumentName(documentCode), getCountryName(countryCode), countryCode);
        } else {
          // If no documents exist, show upload popup
          handleViewDocuments([], getDocumentName(documentCode), getCountryName(countryCode), countryCode);
        }
        break;
      case "download":
        // Get all relevant documents (including special requirements)
        const allRelevantDocs = getAllRelevantDocuments(countryCode, documentCode);
        if (allRelevantDocs.length > 0) {
          const countryName = getCountryName(countryCode);
          const documentName = getDocumentName(documentCode);
          downloadFilesAsZip(allRelevantDocs, countryName, documentName);
        }
        break;
      case "view":
        handleViewDocuments(docs, getDocumentName(documentCode), getCountryName(countryCode), countryCode);
        break;
      case "history":
        const allHistoryDocs = getAllRelevantDocumentsForHistory(countryCode, documentCode);
        setHistoryDialog({
          open: true,
          docs: allHistoryDocs,
          docName: getDocumentName(documentCode),
          countryName: getCountryName(countryCode),
        });
        break;
      default:
        break;
    }

    handleCloseContextMenu();
  };

  const columns = [
    {
      field: "countryName",
      headerName: "Country",
      width: 170,
      headerAlign: "center",
      renderCell: (params) => {
        // Get required document types for this country
        const vatRequiredDocs = documentTypes?.filter((doc) => doc.vatRequired) || [];
        const exciseRequiredDocs = documentTypes?.filter((doc) => doc.edRequired) || [];
        const hasVatRequirements = vatRequiredDocs.length > 0;
        const hasExciseRequirements = exciseRequiredDocs.length > 0;

        // Get country-specific requirements for VAT and Excise documents
        const countryId = params.row.countryId;
        const vatCountryRequirements = [];
        const exciseCountryRequirements = [];

        if (vatRequiredDocs.length > 0) {
          vatRequiredDocs.forEach((doc) => {
            const requirementKey = `${countryId}-${doc.documentTypeId}`;
            const requirementType = specialRequirements[requirementKey];
            if (requirementType && requirementType !== "") {
              vatCountryRequirements.push({
                docName: doc.name,
                requirementType: requirementType,
              });
            }
          });
        }

        if (exciseRequiredDocs.length > 0) {
          exciseRequiredDocs.forEach((doc) => {
            const requirementKey = `${countryId}-${doc.documentTypeId}`;
            const requirementType = specialRequirements[requirementKey];
            if (requirementType && requirementType !== "") {
              exciseCountryRequirements.push({
                docName: doc.name,
                requirementType: requirementType,
              });
            }
          });
        }

        const hasCountrySpecificRequirements = vatCountryRequirements.length > 0 || exciseCountryRequirements.length > 0;

        return (
          <Box
            sx={{
              background: "background.paper",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Typography variant="body2" sx={{ ...getThemeTextColor(theme) }}>
              {params.value}
            </Typography>
            {(hasVatRequirements || hasExciseRequirements || hasCountrySpecificRequirements) && (
              <Tooltip
                title={
                  <Box>
                    {hasVatRequirements && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                          VAT Required:
                        </Typography>
                        {vatRequiredDocs.map((doc, index) => (
                          <Typography key={index} variant="caption" display="block">
                            • {doc.name}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {hasVatRequirements && hasExciseRequirements && <Box sx={{ height: 8 }} />}
                    {hasExciseRequirements && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                          Excise Required:
                        </Typography>
                        {exciseRequiredDocs.map((doc, index) => (
                          <Typography key={index} variant="caption" display="block">
                            • {doc.name}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {hasCountrySpecificRequirements && (hasVatRequirements || hasExciseRequirements) && <Box sx={{ height: 8 }} />}
                    {hasCountrySpecificRequirements && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5, color: "white" }}>
                          Country-Specific Requirements:
                        </Typography>
                        {vatCountryRequirements.map((req, index) => (
                          <Typography key={`vat-${index}`} variant="caption" display="block" sx={{ color: "white" }}>
                            • {req.docName}: {getRequirementDescription(req.requirementType)}
                          </Typography>
                        ))}
                        {exciseCountryRequirements.map((req, index) => (
                          <Typography key={`excise-${index}`} variant="caption" display="block" sx={{ color: "white" }}>
                            • {req.docName}: {getRequirementDescription(req.requirementType)}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                }
                placement="right"
                arrow
              >
                <InfoIcon
                  sx={{
                    fontSize: 16,
                    color: hasCountrySpecificRequirements ? "warning.main" : "primary.main",
                    opacity: 0.7,
                    cursor: "help",
                  }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
      pinToLeft: true,
    },
    {
      field: "vat",
      headerName: "VAT",
      width: 60,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const hasAllVat = vatStatus[params.row.countryId];
        const isLoading = hasAllVat === undefined;

        return (
          <Box
            sx={{
              background: hasAllVat ? "rgba(76, 175, 80, 0.1)" : "rgba(0, 0, 0, 0.04)",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isLoading ? (
              <CircularProgress size={16} />
            ) : hasAllVat ? (
              <Checkbox checked color="success" inputProps={{ "aria-label": "All VAT docs present" }} />
            ) : null}
          </Box>
        );
      },
      pinToLeft: true,
    },
    {
      field: "excise",
      headerName: "Excise",
      width: 60,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const hasAllExcise = exciseStatus[params.row.countryId];
        const isLoading = hasAllExcise === undefined;

        return (
          <Box
            sx={{
              background: hasAllExcise ? "rgba(76, 175, 80, 0.1)" : "rgba(0, 0, 0, 0.04)",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isLoading ? (
              <CircularProgress size={16} />
            ) : hasAllExcise ? (
              <Checkbox checked color="success" inputProps={{ "aria-label": "All Excise docs present" }} />
            ) : null}
          </Box>
        );
      },
      pinToLeft: true,
    },
    ...(documentTypes?.map((doc) => ({
      field: doc.shortCode.toLowerCase(),
      headerName: doc.name,
      width: 145,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const docs = customerDocuments.filter(
          (d) =>
            Number(d.countryId) === Number(params.row.countryId) &&
            Number(d.documentType) === Number(doc.documentTypeId) &&
            Number(d.deleted) === 0
        );
        const allDocs = customerDocuments.filter(
          (d) => Number(d.countryId) === Number(params.row.countryId) && Number(d.documentType) === Number(doc.documentTypeId)
        );

        // Add special requirement documents to allDocs for history purposes
        const countryId = params.row.countryId;
        const requirementKey = `${countryId}-${doc.documentTypeId}`;
        const requirementType = specialRequirements[requirementKey];
        let allRelevantDocs = [...allDocs];

        if (requirementType && requirementType !== "") {
          // Add legalization documents if required
          if (requirementType === "1" || requirementType === "2" || requirementType === "3") {
            const legalizationDocs = customerDocuments.filter(
              (d) => Number(d.countryId) === Number(countryId) && Number(d.documentType) === 998
            );
            allRelevantDocs = [...allRelevantDocs, ...legalizationDocs];
          }

          // Add KvK Translated Documents if required
          if (requirementType === "3" || requirementType === "4") {
            const kvkDocs = customerDocuments.filter(
              (d) => Number(d.countryId) === Number(countryId) && Number(d.documentType) === 999
            );
            allRelevantDocs = [...allRelevantDocs, ...kvkDocs];
          }
        }

        const latestDoc = docs.length > 0 ? docs.reduce((a, b) => (a.documentVersion > b.documentVersion ? a : b)) : null;

        // Count only deleted documents for history
        const deletedDocs = allRelevantDocs.filter((doc) => doc.deleted === 1);
        const hasMultipleVersions = deletedDocs.length > 0;

        // Get pre-calculated status
        const status = documentStatus[`${params.row.countryId}-${doc.documentTypeId}`];
        const isStatusLoading = status === undefined;
        const hasAllRequired = status === true;
        const isPartial = status === "partial";
        const isExpired = status === "expired";
        const isExpiring = status === "expiring";

        return (
          <Box
            sx={{
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "row",
              gap: 1,
              padding: 1,
            }}
            onDragOver={handleCellDragOver}
            onDragLeave={handleCellDragLeave}
            onDrop={(e) => handleCellDrop(e, params.row.countryCode, doc.shortCode.toLowerCase())}
            onContextMenu={(e) => handleContextMenu(e, params.row.countryCode, doc.shortCode.toLowerCase(), allRelevantDocs)}
          >
            {/* Status Indicator */}
            {isStatusLoading ? (
              <CircularProgress size={16} />
            ) : isExpired ? (
              <Tooltip title={`Document has expired (${docs.length} version${docs.length > 1 ? "s" : ""})`}>
                <Chip
                  icon={<ErrorIcon />}
                  label="Expired"
                  color="error"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.7rem", height: 20, minWidth: 70 }}
                />
              </Tooltip>
            ) : isExpiring ? (
              <Tooltip title={`Document expires within 30 days (${docs.length} version${docs.length > 1 ? "s" : ""})`}>
                <Chip
                  icon={<WarningIcon />}
                  label="Expiring"
                  color="warning"
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: "0.7rem",
                    height: 20,
                    minWidth: 70,
                    borderColor: "#ff9800",
                    "& .MuiChip-label": {
                      color: "#f57c00",
                    },
                  }}
                />
              </Tooltip>
            ) : isPartial ? (
              <Tooltip
                title={`Some documents present, but not all required documents (${docs.length} version${docs.length > 1 ? "s" : ""})`}
              >
                <Chip
                  icon={<WarningIcon />}
                  label="Partial"
                  color="warning"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.7rem", height: 20, minWidth: 70 }}
                />
              </Tooltip>
            ) : hasAllRequired ? (
              <Tooltip title={`All required documents present (${docs.length} version${docs.length > 1 ? "s" : ""})`}>
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Present"
                  color="success"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.7rem", height: 20, minWidth: 70 }}
                />
              </Tooltip>
            ) : (
              <Tooltip title="No document uploaded">
                <Chip
                  label="N/A"
                  color="default"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.7rem", height: 20, minWidth: 70 }}
                />
              </Tooltip>
            )}

            {/* View/Upload Icon */}
            <Tooltip title={latestDoc ? "View document" : "Upload document"}>
              <IconButton
                size="small"
                sx={{
                  opacity: 0.7,
                  "&:hover": { opacity: 1 },
                  padding: 0.5,
                  color: latestDoc ? "primary.main" : "warning.main",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (latestDoc) {
                    handleViewDocuments(docs, doc.name, params.row.countryName);
                  } else {
                    // Open document preview with upload interface for required documents
                    handleViewDocuments([], doc.name, params.row.countryName, params.row.countryCode);
                  }
                }}
              >
                {latestDoc || isPartial ? <VisibilityIcon fontSize="small" /> : <CloudUploadIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            {/* Action Menu */}
            <Tooltip title="Right-click for more options">
              <IconButton
                size="small"
                sx={{
                  opacity: 0.7,
                  "&:hover": { opacity: 1 },
                  padding: 0.5,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleContextMenu(e, params.row.countryCode, doc.shortCode.toLowerCase(), allRelevantDocs);
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    })) || []),
  ];

  const rows =
    countryData?.map((country) => ({
      id: country.countryId,
      countryId: country.countryId,
      countryCode: country.countryCode,
      countryName: country.countryName,
    })) || [];

  return (
    <>
      <DataGrid
        key={refreshKey}
        rows={rows}
        columns={columns}
        pageSize={rows.length}
        density="compact"
        components={{ Toolbar: GridToolbar }}
        componentsProps={{
          toolbar: {
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 250 },
          },
        }}
        disableSelectionOnClick
        hideFooter
        sx={{
          height: "calc(100vh - 545px)",
          width: "100%",
          borderRadius: 2,
          backgroundColor: "background.paper",
          boxShadow: 3,
          border: "1px solid",
          borderColor: "divider",
          "& .MuiDataGrid-cell:hover": {
            backgroundColor: "action.hover",
          },
          "& .MuiDataGrid-cell": {
            padding: "8px 4px",
            borderBottom: "1px solid",
            borderBottomColor: "divider",
          },
          "& .MuiDataGrid-columnHeader": {
            padding: "16px 8px !important",
            backgroundColor: "background.paper",
            borderBottom: "2px solid",
            borderBottomColor: "primary.main",
            fontWeight: 600,
            fontSize: "0.875rem",
            minHeight: "56px !important",
            alignItems: "center",
            display: "flex !important",
            lineHeight: "1.2 !important",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            overflow: "visible !important",
            textOverflow: "unset !important",
            whiteSpace: "normal !important",
            lineHeight: "1.2 !important",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "action.hover",
          },
          "& .MuiDataGrid-row:nth-of-type(even)": {
            backgroundColor: "rgba(255, 255, 255, 0.02)",
          },
          marginTop: 0,
        }}
      />

      {/* Default Upload Dialog */}
      <Dialog open={uploadDialog.isOpen} onClose={handleUploadCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          Upload {getDocumentName(uploadDialog.documentCode)} for {getCountryName(uploadDialog.countryCode)}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              border: "2px dashed",
              borderColor: isDragging ? "primary.main" : "grey.300",
              borderRadius: 1,
              p: 3,
              textAlign: "center",
              bgcolor: isDragging ? "action.hover" : "background.paper",
              cursor: "pointer",
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input type="file" ref={fileInputRef} onChange={handleFileSelect} style={{ display: "none" }} />
            {selectedFile ? (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Selected File:
                </Typography>
                <Typography variant="body1" color="primary">
                  {selectedFile.name}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Drag and drop a file here, or click to select a file
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Supported formats: PDF, DOC, DOCX
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadCancel}>Cancel</Button>
          <Button onClick={handleUploadConfirm} variant="contained" color="primary" disabled={!selectedFile}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Time Restriction Upload Dialog */}
      <Dialog open={timeRestrictionDialog.isOpen} onClose={handleTimeRestrictionCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          Upload {getDocumentName(timeRestrictionDialog.documentCode)} for {getCountryName(timeRestrictionDialog.countryCode)} (Time
          Restricted)
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box
                sx={{
                  border: "2px dashed",
                  borderColor: isDragging ? "primary.main" : "grey.300",
                  borderRadius: 1,
                  p: 3,
                  textAlign: "center",
                  bgcolor: isDragging ? "action.hover" : "background.paper",
                  cursor: "pointer",
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input type="file" ref={fileInputRef} onChange={handleFileSelect} style={{ display: "none" }} />
                {selectedFile ? (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Selected File:
                    </Typography>
                    <Typography variant="body1" color="primary">
                      {selectedFile.name}
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Drag and drop a file here, or click to select a file
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Supported formats: PDF, DOC, DOCX
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Valid From"
                type="datetime-local"
                value={timeRestrictionDialog.validFrom || ""}
                onChange={(e) => setTimeRestrictionDialog((prev) => ({ ...prev, validFrom: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Valid Till"
                type="datetime-local"
                value={timeRestrictionDialog.validTill || ""}
                onChange={(e) => setTimeRestrictionDialog((prev) => ({ ...prev, validTill: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTimeRestrictionCancel}>Cancel</Button>
          <Button onClick={handleTimeRestrictionUpload} variant="contained" color="primary" disabled={!selectedFile}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Special Requirements Upload Dialog */}
      <Dialog open={specialRequirementsDialog.isOpen} onClose={handleSpecialRequirementsCancel} maxWidth="md" fullWidth>
        <DialogTitle>
          Upload {getDocumentName(specialRequirementsDialog.documentCode)} for {getCountryName(specialRequirementsDialog.countryCode)}{" "}
          (Special Requirements)
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                Requirement Type:{" "}
                {specialRequirementsDialog.requirementType === "1"
                  ? "Legalization (Document Type 998)"
                  : specialRequirementsDialog.requirementType === "2"
                  ? "Legalization (Document Type 998)"
                  : specialRequirementsDialog.requirementType === "3"
                  ? "Legalization (Document Type 998) + required KvK Translated Documents (Document Type 999)"
                  : specialRequirementsDialog.requirementType === "4"
                  ? "Required KvK Translated Documents (Document Type 999)"
                  : "Unknown requirement type"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Main Document *
              </Typography>
              <Box
                sx={{
                  border: "2px dashed",
                  borderColor: specialRequirementsDialog.files.mainDocument ? "success.main" : "grey.300",
                  borderRadius: 1,
                  p: 3,
                  textAlign: "center",
                  bgcolor: specialRequirementsDialog.files.mainDocument ? "success.light" : "background.paper",
                  cursor: "pointer",
                }}
                onClick={() => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.onchange = (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setSpecialRequirementsDialog((prev) => ({
                        ...prev,
                        files: { ...prev.files, mainDocument: file },
                      }));
                    }
                  };
                  input.click();
                }}
              >
                {specialRequirementsDialog.files.mainDocument ? (
                  <Typography variant="body1" color="success.main">
                    {specialRequirementsDialog.files.mainDocument.name}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Click to select main document
                  </Typography>
                )}
              </Box>
            </Grid>

            {(specialRequirementsDialog.requirementType === "1" ||
              specialRequirementsDialog.requirementType === "2" ||
              specialRequirementsDialog.requirementType === "3") && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Legalization Document{" "}
                  {specialRequirementsDialog.requirementType === "2" || specialRequirementsDialog.requirementType === "3" ? "*" : ""}
                </Typography>
                <Box
                  sx={{
                    border: "2px dashed",
                    borderColor: specialRequirementsDialog.files.legalizationDocument ? "success.main" : "grey.300",
                    borderRadius: 1,
                    p: 3,
                    textAlign: "center",
                    bgcolor: specialRequirementsDialog.files.legalizationDocument ? "success.light" : "background.paper",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.onchange = (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setSpecialRequirementsDialog((prev) => ({
                          ...prev,
                          files: { ...prev.files, legalizationDocument: file },
                        }));
                      }
                    };
                    input.click();
                  }}
                >
                  {specialRequirementsDialog.files.legalizationDocument ? (
                    <Typography variant="body1" color="success.main">
                      {specialRequirementsDialog.files.legalizationDocument.name}
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Click to select legalization document
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}

            {(specialRequirementsDialog.requirementType === "3" || specialRequirementsDialog.requirementType === "4") && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  KvK Translated Documents *
                </Typography>
                <Box
                  sx={{
                    border: "2px dashed",
                    borderColor: specialRequirementsDialog.files.kvkDocument ? "success.main" : "grey.300",
                    borderRadius: 1,
                    p: 3,
                    textAlign: "center",
                    bgcolor: specialRequirementsDialog.files.kvkDocument ? "success.light" : "background.paper",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.onchange = (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setSpecialRequirementsDialog((prev) => ({
                          ...prev,
                          files: { ...prev.files, kvkDocument: file },
                        }));
                      }
                    };
                    input.click();
                  }}
                >
                  {specialRequirementsDialog.files.kvkDocument ? (
                    <Typography variant="body1" color="success.main">
                      {specialRequirementsDialog.files.kvkDocument.name}
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Click to select KvK Translated Documents
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}

            {specialRequirementsDialog.hasTimeRestriction && (
              <>
                <Grid item xs={6}>
                  <TextField
                    label="Valid From"
                    type="datetime-local"
                    value={specialRequirementsDialog.validFrom || ""}
                    onChange={(e) => setSpecialRequirementsDialog((prev) => ({ ...prev, validFrom: e.target.value }))}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Valid Till"
                    type="datetime-local"
                    value={specialRequirementsDialog.validTill || ""}
                    onChange={(e) => setSpecialRequirementsDialog((prev) => ({ ...prev, validTill: e.target.value }))}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSpecialRequirementsCancel}>Cancel</Button>
          <Button
            onClick={handleSpecialRequirementsUpload}
            variant="contained"
            color="primary"
            disabled={
              !specialRequirementsDialog.files.mainDocument ||
              ((specialRequirementsDialog.requirementType === "1" ||
                specialRequirementsDialog.requirementType === "2" ||
                specialRequirementsDialog.requirementType === "3") &&
                !specialRequirementsDialog.files.legalizationDocument) ||
              (specialRequirementsDialog.requirementType === "3" && !specialRequirementsDialog.files.kvkDocument) ||
              (specialRequirementsDialog.requirementType === "4" && !specialRequirementsDialog.files.kvkDocument)
            }
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialog.open} onClose={() => setHistoryDialog({ ...historyDialog, open: false })} maxWidth="md" fullWidth>
        <DialogTitle>
          History for {historyDialog.docName} in {historyDialog.countryName}
        </DialogTitle>
        <DialogContent>
          {historyDialog.docs.length === 0 ? (
            <Typography>No history available.</Typography>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={() => downloadFilesAsZip(historyDialog.docs, historyDialog.countryName, historyDialog.docName)}
                >
                  Download All as ZIP
                </Button>
              </Box>
              {(() => {
                // Group documents by document type
                const groupedDocs = historyDialog.docs.reduce((acc, doc) => {
                  const docType = doc.documentType;
                  if (!acc[docType]) {
                    acc[docType] = [];
                  }
                  acc[docType].push(doc);
                  return acc;
                }, {});

                // Sort document types for consistent display
                const sortedDocTypes = Object.keys(groupedDocs).sort((a, b) => Number(a) - Number(b));

                return sortedDocTypes.map((docType) => {
                  const docs = groupedDocs[docType].sort((a, b) => {
                    // Sort by upload date (newest first), then by version (newest first)
                    const dateA = new Date(a.uploadDate);
                    const dateB = new Date(b.uploadDate);
                    if (dateA.getTime() !== dateB.getTime()) {
                      return dateB.getTime() - dateA.getTime();
                    }
                    return b.documentVersion - a.documentVersion;
                  });
                  const docTypeName = getDocumentName(Number(docType));
                  const isDeleted = docs.some((doc) => doc.deleted === 1);

                  return (
                    <Box key={docType} sx={{ mb: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 1,
                          color: isDeleted ? "text.secondary" : "primary.main",
                          fontWeight: "bold",
                          borderBottom: "2px solid",
                          borderColor: isDeleted ? "text.secondary" : "primary.main",
                          pb: 0.5,
                        }}
                      >
                        {docTypeName}
                      </Typography>
                      {docs.map((doc) => (
                        <Box
                          key={doc.customerDocumentId}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={1}
                          sx={{
                            opacity: doc.deleted === 1 ? 0.6 : 1,
                            backgroundColor: doc.deleted === 1 ? "action.disabled" : "transparent",
                            borderRadius: 1,
                            p: 1,
                          }}
                        >
                          <Box>
                            <Typography>
                              Version {doc.documentVersion} - {moment(doc.uploadDate).format("YYYY-MM-DD HH:mm")} -{" "}
                              {historyFilenames[doc.fileId] || ""}
                              {doc.deleted === 1 && " (Deleted)"}
                            </Typography>
                            {doc.validFrom && doc.validTill && (
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                Valid: {moment(doc.validFrom).format("YYYY-MM-DD")} - {moment(doc.validTill).format("YYYY-MM-DD")}
                              </Typography>
                            )}
                            {doc.uploadedBy && (
                              <Typography variant="caption" sx={{ color: "text.secondary" }} display="block">
                                Uploaded by: {getUserName(doc.uploadedBy)}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <IconButton
                              color="primary"
                              onClick={() => handleHistoryDocumentPreview(doc, docTypeName, historyDialog.countryName)}
                              aria-label={`View version ${doc.documentVersion}`}
                              size="small"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              color={doc.deleted === 1 ? "warning" : "success"}
                              onClick={() => downloadFile(doc.fileId)}
                              aria-label={`Download version ${doc.documentVersion}`}
                              size="small"
                            >
                              <FileDownloadIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  );
                });
              })()}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialog({ ...historyDialog, open: false })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* View Documents Dialog */}
      <Dialog
        open={viewDialog.open}
        onClose={handleViewDialogClose}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            height: "80vh",
            maxHeight: "80vh",
          },
          "& .MuiDialogContent-root": {
            overflowY: "hidden",
          },
        }}
      >
        <DialogTitle>
          Documents for {viewDialog.countryName} - {viewDialog.docName}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ height: "calc(80vh - 100px)" }}>
            {/* Left side - Document List */}
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }} gutterBottom>
                  Showing all required documents for this country and document type combination
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={() => downloadFilesAsZip(viewDialog.docs, viewDialog.countryName, viewDialog.docName)}
                  size="small"
                  fullWidth
                >
                  Download All as ZIP
                </Button>
              </Box>
              <Box
                sx={{
                  height: "calc(80vh - 120px)",
                  overflowY: "auto",
                  "&::-webkit-scrollbar": { display: "none" },
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
              >
                {(() => {
                  // Create a combined list of documents and upload placeholders in the correct order
                  const country = countryData?.find((c) => c.countryCode === viewDialog.countryCode);
                  const mainDocType = documentTypes?.find((doc) => doc.name === viewDialog.docName);

                  if (!country || !mainDocType) return null;

                  // Define the expected document types in order
                  const expectedDocTypes = [{ documentTypeId: mainDocType.documentTypeId, name: mainDocType.name, type: "main" }];

                  // Add special requirements only if they are actually required for this country/document combination
                  const requirementKey = `${country.countryId}-${mainDocType.documentTypeId}`;
                  const requirementType = specialRequirements[requirementKey];

                  if (requirementType) {
                    // Add legalization document if required
                    if (["1", "2", "3"].includes(requirementType)) {
                      expectedDocTypes.push({
                        documentTypeId: 998,
                        name: "Legalization Document",
                        type: "legalization",
                      });
                    }

                    // Add KvK document if required
                    if (["3", "4"].includes(requirementType)) {
                      expectedDocTypes.push({
                        documentTypeId: 999,
                        name: "KvK Translated Documents",
                        type: "kvk",
                      });
                    }
                  }

                  // Create combined list
                  const combinedList = [];

                  expectedDocTypes.forEach((expectedDoc) => {
                    // Check if we have this document type
                    const existingDoc = viewDialog.docs.find((doc) => Number(doc.documentType) === Number(expectedDoc.documentTypeId));

                    if (existingDoc) {
                      // Add existing document
                      combinedList.push({
                        type: "document",
                        data: existingDoc,
                      });
                    } else {
                      // Add upload placeholder
                      combinedList.push({
                        type: "upload",
                        data: expectedDoc,
                      });
                    }
                  });

                  return combinedList.map((item, index) => {
                    if (item.type === "document") {
                      const doc = item.data;
                      return (
                        <Box
                          key={doc.customerDocumentId}
                          onClick={() => handleDocumentPreviewSelect(doc)}
                          sx={{
                            cursor: "pointer",
                            p: 2,
                            mb: 1,
                            border: "1px solid",
                            borderColor:
                              selectedDocumentForPreview?.customerDocumentId === doc.customerDocumentId ? "primary.main" : "divider",
                            borderRadius: 1,
                            bgcolor:
                              selectedDocumentForPreview?.customerDocumentId === doc.customerDocumentId
                                ? "primary.light"
                                : "background.paper",
                            "&:hover": {
                              bgcolor:
                                selectedDocumentForPreview?.customerDocumentId === doc.customerDocumentId
                                  ? "primary.light"
                                  : "action.hover",
                            },
                          }}
                        >
                          <Typography variant="subtitle2" gutterBottom color="primary">
                            {getDocumentName(doc.documentType)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "text.secondary" }} gutterBottom>
                            Uploaded: {moment(doc.uploadDate).format("YYYY-MM-DD HH:mm")}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "text.secondary" }} gutterBottom>
                            File: {historyFilenames[doc.fileId] || "Unknown filename"}
                          </Typography>
                          {doc.hasTimeRestriction && (
                            <>
                              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                Valid From: {doc.validFrom ? moment(doc.validFrom).format("YYYY-MM-DD") : "Not set"}
                              </Typography>
                              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                Valid Till: {doc.validTill ? moment(doc.validTill).format("YYYY-MM-DD") : "Not set"}
                              </Typography>
                            </>
                          )}
                          {doc.uploadedBy && (
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                              Uploaded by: {getUserName(doc.uploadedBy)}
                            </Typography>
                          )}
                        </Box>
                      );
                    } else {
                      // Upload placeholder - use RequiredDocumentsUpload component for this specific document
                      const uploadDoc = item.data;
                      return (
                        <RequiredDocumentsUpload
                          key={`upload-${uploadDoc.documentTypeId}-${viewDialog.docs.length}`}
                          countryName={viewDialog.countryName}
                          countryCode={viewDialog.countryCode}
                          docName={viewDialog.docName}
                          onUpload={handleUpload}
                          onClose={handleViewDialogClose}
                          documentTypes={documentTypes}
                          countryData={countryData}
                          existingDocs={viewDialog.docs}
                          showUploadInterface={false}
                          allCountryRequirements={allCountryRequirements}
                          customerDocuments={customerDocuments}
                          customerId={customerId}
                          inlineMode={true}
                          specificDocumentType={uploadDoc.documentTypeId}
                          specialRequirements={specialRequirements}
                          setNotify={setNotify}
                          onShowDateInputDialog={(data) => {
                            // Get reference to the RequiredDocumentsUpload component's handleDateInputCancel
                            const resetCallback = () => {
                              // This will be called when the date input dialog is cancelled
                              // The RequiredDocumentsUpload component will handle the reset
                            };

                            setDateInputDialog({
                              open: true,
                              ...data,
                              onCancel: resetCallback,
                            });
                          }}
                          onDateInputCancel={(callback) => setResetCallback(() => callback)}
                        />
                      );
                    }
                  });
                })()}
              </Box>
            </Grid>

            {/* Right side - Document Preview */}
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  height: "calc(80vh - 120px)",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 2,
                  pb: 0,
                  overflow: "hidden",
                }}
              >
                {selectedDocumentForPreview ? (
                  <>
                    <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="h6" color="primary">
                        Preview: {historyFilenames[selectedDocumentForPreview.fileId] || "Document"}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          variant="outlined"
                          startIcon={<FileDownloadIcon />}
                          onClick={() => downloadFile(selectedDocumentForPreview.fileId)}
                          size="small"
                        >
                          Download
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => {
                            setConfirmDialog({
                              isOpen: true,
                              title: "Delete Document",
                              subTitle: `Are you sure you want to delete "${
                                historyFilenames[selectedDocumentForPreview.fileId] || "this document"
                              }"? This action cannot be undone.`,
                              onConfirm: async () => {
                                // Clear preview immediately
                                if (documentPreviewUrl) {
                                  URL.revokeObjectURL(documentPreviewUrl);
                                  setDocumentPreviewUrl(null);
                                }
                                setSelectedDocumentForPreview(null);

                                // Delete document first
                                await handleDeleteDocument(selectedDocumentForPreview.customerDocumentId);

                                // Remove document from view dialog after successful delete
                                const updatedDocs = viewDialog.docs.filter(
                                  (doc) => doc.customerDocumentId !== selectedDocumentForPreview.customerDocumentId
                                );
                                setViewDialog((prev) => ({
                                  ...prev,
                                  docs: updatedDocs,
                                }));

                                setConfirmDialog({ isOpen: false, title: "", subTitle: "", onConfirm: null });
                              },
                            });
                          }}
                          size="small"
                        >
                          Delete
                        </Button>
                      </Box>
                    </Box>

                    {documentPreviewUrl ? (
                      <Box
                        sx={{
                          height: "calc(100% - 20px)",
                          overflow: "auto",
                          "&::-webkit-scrollbar": { display: "none" },
                          msOverflowStyle: "none",
                          scrollbarWidth: "none",
                          width: "100%",
                          maxWidth: "100%",
                        }}
                      >
                        {selectedDocumentForPreview.mimeType?.includes("pdf") ? (
                          <iframe
                            src={documentPreviewUrl}
                            width="100%"
                            height="100%"
                            style={{
                              border: "none",
                              scrollbarWidth: "none",
                              msOverflowStyle: "none",
                              maxWidth: "100%",
                              overflow: "hidden",
                            }}
                            title="Document Preview"
                          />
                        ) : selectedDocumentForPreview.mimeType?.includes("image") ? (
                          <img
                            src={documentPreviewUrl}
                            alt="Document Preview"
                            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <Typography variant="body1" sx={{ color: "text.secondary" }}>
                              Preview not available for this file type
                            </Typography>
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                              File type: {selectedDocumentForPreview.mimeType || "Unknown"}
                            </Typography>
                            <Button
                              variant="contained"
                              startIcon={<FileDownloadIcon />}
                              onClick={() => downloadFile(selectedDocumentForPreview.fileId)}
                            >
                              Download to View
                            </Button>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography sx={{ ...getThemeTextColor(theme) }}>Loading preview...</Typography>
                      </Box>
                    )}
                  </>
                ) : viewDialog.docs.length === 0 ? (
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 4,
                      textAlign: "center",
                    }}
                  >
                    <CloudUploadIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
                    <Typography variant="h6" sx={{ color: "text.secondary" }} gutterBottom>
                      No documents available
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }} gutterBottom>
                      Use the upload interface on the left to add required documents
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography sx={{ color: "text.secondary" }}>Select a document to preview</Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleViewDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        open={contextMenu.mouseY !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu.mouseY !== null && contextMenu.mouseX !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {contextMenu.docs.length === 0 && (
          <MenuItem onClick={() => handleContextMenuAction("upload")} disabled={false}>
            <CloudUploadIcon sx={{ mr: 1 }} />
            Upload Document
          </MenuItem>
        )}
        {contextMenu.docs.length > 0 && (
          <MenuItem onClick={() => handleContextMenuAction("download")}>
            <FileDownloadIcon sx={{ mr: 1 }} />
            Download All ({getAllRelevantDocuments(contextMenu.countryCode, contextMenu.documentCode).length})
          </MenuItem>
        )}
        {contextMenu.docs.length > 0 && (
          <MenuItem onClick={() => handleContextMenuAction("view")}>
            <VisibilityIcon sx={{ mr: 1 }} />
            View Document
          </MenuItem>
        )}
        {(() => {
          const deletedDocs = contextMenu.docs.filter((doc) => doc.deleted === 1);
          return (
            deletedDocs.length > 0 && (
              <MenuItem onClick={() => handleContextMenuAction("history")}>
                <HistoryIcon sx={{ mr: 1 }} />
                View History ({deletedDocs.length} deleted version{deletedDocs.length > 1 ? "s" : ""})
              </MenuItem>
            )
          );
        })()}
      </Menu>

      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />

      {/* Date Input Dialog */}
      <Dialog open={dateInputDialog.open} onClose={handleDateInputCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" color="primary">
            Time Restrictions Required
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary" }} gutterBottom>
            Please provide the validity period for this document:
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                label="Valid From"
                type="date"
                value={timeRestrictions[dateInputDialog.documentType]?.validFrom || ""}
                onChange={(e) => {
                  handleTimeRestrictionChange(dateInputDialog.documentType, "validFrom", e.target.value);
                }}
                fullWidth
                size="small"
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Valid Till"
                type="date"
                value={timeRestrictions[dateInputDialog.documentType]?.validTill || ""}
                onChange={(e) => {
                  handleTimeRestrictionChange(dateInputDialog.documentType, "validTill", e.target.value);
                }}
                fullWidth
                size="small"
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDateInputCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDateInputConfirm} variant="contained" color="primary">
            Upload Document
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Document Preview Dialog */}
      <Dialog
        open={historyPreviewDialog.open}
        onClose={handleHistoryPreviewDialogClose}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            height: "80vh",
            maxHeight: "80vh",
          },
          "& .MuiDialogContent-root": {
            overflowY: "hidden",
          },
        }}
      >
        <DialogTitle>
          Preview: {historyFilenames[historyPreviewDialog.doc?.fileId] || "Document"} - Version{" "}
          {historyPreviewDialog.doc?.documentVersion}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              height: "calc(80vh - 100px)",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
              pb: 0,
              overflow: "hidden",
            }}
          >
            {historyPreviewUrl ? (
              <Box
                sx={{
                  height: "calc(100% - 20px)",
                  overflow: "auto",
                  "&::-webkit-scrollbar": { display: "none" },
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                  width: "100%",
                  maxWidth: "100%",
                }}
              >
                {historyPreviewDialog.doc?.mimeType?.includes("pdf") ? (
                  <iframe
                    src={historyPreviewUrl}
                    width="100%"
                    height="100%"
                    style={{
                      border: "none",
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                      maxWidth: "100%",
                      overflow: "hidden",
                    }}
                    title="Document Preview"
                  />
                ) : historyPreviewDialog.doc?.mimeType?.includes("image") ? (
                  <img
                    src={historyPreviewUrl}
                    alt="Document Preview"
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Typography variant="body1" sx={{ color: "text.secondary" }}>
                      Preview not available for this file type
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      File type: {historyPreviewDialog.doc?.mimeType || "Unknown"}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<FileDownloadIcon />}
                      onClick={() => downloadFile(historyPreviewDialog.doc?.fileId)}
                    >
                      Download to View
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ ...getThemeTextColor(theme) }}>Loading preview...</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleHistoryPreviewDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MSRDocumentsTab;
